<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import Draggable from 'vuedraggable';
import { debounce } from '@chatwoot/utils';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import FeatureLockedScreen from 'dashboard/components/FeatureLockedScreen.vue';

import axios from 'axios';
import PipelinesApi from 'dashboard/api/pipelines';
import { productsApi } from 'dashboard/api/products';
import automationsApi from 'dashboard/api/automation';
import ContactAPI from 'dashboard/api/contacts';
import MessageApi from 'dashboard/api/inbox/message';
import { conversionTypesApi } from 'dashboard/api/conversions';
import { pipelineItemDocumentsApi } from 'dashboard/api/pipelineItemDocuments';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ScheduleMessageDialog from 'dashboard/components/widgets/conversation/ScheduleMessageDialog.vue';
import InstagramLeadScoreCard from 'dashboard/components-next/instagram/InstagramLeadScoreCard.vue';
import AddAutomationRule from 'dashboard/routes/dashboard/settings/automation/AddAutomationRule.vue';
import EditAutomationRule from 'dashboard/routes/dashboard/settings/automation/EditAutomationRule.vue';
import CardAttributeInlineEditor from './CardAttributeInlineEditor.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';

const { t } = useI18n();
const { accountScopedRoute, accountId } = useAccount();
const { isAdmin } = useAdmin();
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');
const hasCrm = computed(() => isFeatureEnabled.value(accountId.value, 'crm'));
const hasProductsCatalog = computed(() => isFeatureEnabled.value(accountId.value, 'products_catalog'));
const products = ref([]);

const CATEGORY_MAP = {
  pension: { label: 'Previdência', color: 'bg-emerald-100 text-emerald-700' },
  risk: { label: 'Risco', color: 'bg-rose-100 text-rose-700' },
  contribution: { label: 'Aporte/Portabilidade', color: 'bg-amber-100 text-amber-700' },
  general: { label: 'Geral', color: 'bg-n-alpha-2 text-n-slate-11' },
};

const store = useStore();

watch(hasProductsCatalog, enabled => {
  if (enabled && !products.value.length) {
    productsApi.list({ active_only: true })
      .then(r => { products.value = r.data?.payload || r.data || []; })
      .catch(() => {});
  }
}, { immediate: true });
const router = useRouter();

const inboxes = useMapGetter('inboxes/getInboxes');

const isLoading = ref(false);
const isSaving = ref(false);
const isAddingCard = ref(false);
const isLoadingContactDetails = ref(false);
const isLoadingMessages = ref(false);

const pipelines = ref([]);
const selectedPipelineId = ref(null);
const boardStages = ref([]);

const filters = reactive({
  inbox_id: '',
  q: '',
  moved_from: '',
  moved_to: '',
  return_date_filter: '',
});

const hasPipelines = computed(() => pipelines.value.length > 0);

const createPipelineDialogRef = ref(null);
const createStageDialogRef = ref(null);
const addCardDialogRef = ref(null);
const deletePipelineDialogRef = ref(null);
const deleteStageDialogRef = ref(null);
const scheduleDialogRef = ref(null);
const showAddAutomationModal = ref(false);
const automationPreset = ref(null);

const pipelineForm = reactive({
  name: '',
  description: '',
  color: '',
  icon: '',
});

const stageForm = reactive({
  name: '',
  color: '',
});

const addCardSearch = ref('');
const addCardCandidates = ref([]);
const isAddCardDialogOpen = ref(false);
const targetStageId = ref(null);

// ─── Quick contact creation ───
const showQuickContactDialog = ref(false);
const quickContactForm = reactive({ name: '', phone: '', assignee_id: null });
const quickContactSaving = ref(false);

async function createQuickContact() {
  if (!quickContactForm.name.trim() || !quickContactForm.phone) return;
  quickContactSaving.value = true;
  try {
    const res = await ContactAPI.create({
      name: quickContactForm.name.trim(),
      phone_number: quickContactForm.phone,
    });
    const contact = res.data?.payload?.contact || res.data;

    if (selectedPipelineId.value) {
      const stages = pipelineStages.value[selectedPipelineId.value] || [];
      if (stages.length) {
        const itemPayload = {
          item_type: 'Contact',
          item_id: contact.id,
          pipeline_stage_id: stages[0].id,
        };
        if (quickContactForm.assignee_id) {
          itemPayload.added_by_id = quickContactForm.assignee_id;
        }
        await PipelinesApi.addItem(selectedPipelineId.value, itemPayload);
      }
    }

    showQuickContactDialog.value = false;
    quickContactForm.name = '';
    quickContactForm.phone = '';
    quickContactForm.assignee_id = null;
    await loadBoard();
    useAlert('Contato criado com sucesso');
  } catch (e) {
    const msg = e?.response?.data?.error || e?.response?.data?.message || e?.response?.data?.errors?.join(', ') || 'Erro ao criar contato';
    useAlert(msg);
    console.error('Quick contact error:', e?.response?.data || e);
  } finally {
    quickContactSaving.value = false;
  }
}
const isContactDetailsOpen = ref(false);
const isPipelineSettingsOpen = ref(false);
const pipelineManagerQuery = ref('');
const pipelineManagerSort = ref('updated_desc');
const pipelineManagerViewMode = ref('cards');
const lastOpenedPipelineId = ref(null);
const collapsedFolders = ref(new Set());
const editingFolder = ref(null);
const editingFolderValue = ref('');
const showNewFolderInput = ref(false);
const newFolderName = ref('');

const selectedPipeline = computed(() =>
  pipelines.value.find(pipeline => pipeline.id === selectedPipelineId.value)
);
const pipelineSettingsForm = reactive({
  name: '',
  description: '',
  color: '',
  icon: '',
  restrictedAgents: [],
  card_display_attributes: [],
  settings: {
    alert_enabled: false,
    alert_whatsapp_inbox_id: '',
    alert_whatsapp_numbers_raw: '',
  },
});

const agents = useMapGetter('agents/getAgents');
const isLoadingAgents = ref(false);

const selectedContactCard = ref(null);
const selectedContact = ref(null);
const contactConversations = ref([]);
const selectedConversationId = ref(null);
const conversationMessages = ref([]);
const isEditingContact = ref(false);
const isSavingContact = ref(false);
const contactEditForm = reactive({
  name: '',
  email: '',
  phone_number: '',
});
const contactLabels = ref([]);
const newContactLabel = ref('');
const isUpdatingLabels = ref(false);
const stageSettings = ref([]);
const stagePendingDelete = ref(null);
const conversionTypes = ref([]);

// Attribute automations state
const expandedStageConfigPanel = ref(null);
const gateErrorDialog = ref(null);
const gateErrorMissingAttrs = ref([]);
const allCustomAttributes = computed(() =>
  store.getters['attributes/getAttributes'] || []
);

const editingDealCard = ref(null);
const dealValueInput = ref('');

// ─── Feature 1: Notes ───
const contactNotes = ref([]);
const itemNotes = ref([]);
const newNoteContent = ref('');
const isSavingNote = ref(false);

// ─── Feature 2: Funnel Management ───
const itemMoves = ref([]);
const isLoadingMoves = ref(false);
const modalMoveTargetStageId = ref(null);
const modalMoveNotes = ref('');
const isMovingFromModal = ref(false);

// ─── Feature 3: Return Date ───
const returnDateInput = ref('');
const isSavingReturnDate = ref(false);

// ─── Feature 5: Tasks Checklist ───
const newTaskTitle = ref('');
const newTaskDueDate = ref('');

// ─── Feature 7: Product custom pricing ───
const productValues = ref({});
const computedDealValue = computed(() =>
  Object.values(productValues.value).reduce((sum, v) => sum + (parseFloat(v) || 0), 0)
);
const computedCommission = computed(() => {
  let total = 0;
  const selectedIds = selectedContactCard.value?.product_ids || [];
  selectedIds.forEach(pid => {
    const product = products.value.find(p => p.id === pid);
    if (!product) return;
    const customVal = parseFloat(productValues.value[pid]) || 0;
    if (product.commission_type === 'fixed') {
      total += product.commission_value || 0;
    } else {
      total += customVal * ((product.commission_value || 0) / 100);
    }
  });
  return total;
});

// ─── Modal tab state ───
const modalActiveTab = ref('conversations');

// ─── Documents tab state ───
const itemDocuments = ref([]);
const documentsLoading = ref(false);
const docUploading = ref(false);
const newDocTitle = ref('');
const newDocFile = ref(null);
const docFileInputRef = ref(null);

async function loadDocuments(pipelineId, itemId) {
  if (!pipelineId || !itemId) return;
  documentsLoading.value = true;
  try {
    const res = await pipelineItemDocumentsApi.list(pipelineId, itemId);
    itemDocuments.value = res.data || [];
  } catch {
    itemDocuments.value = [];
  } finally {
    documentsLoading.value = false;
  }
}

async function uploadDocument() {
  if (!newDocTitle.value.trim() || !newDocFile.value || !selectedContactCard.value) return;
  docUploading.value = true;
  try {
    await pipelineItemDocumentsApi.upload(
      selectedContactCard.value.pipeline_id,
      selectedContactCard.value.id,
      { title: newDocTitle.value.trim(), file: newDocFile.value }
    );
    newDocTitle.value = '';
    newDocFile.value = null;
    if (docFileInputRef.value) docFileInputRef.value.value = '';
    await loadDocuments(selectedContactCard.value.pipeline_id, selectedContactCard.value.id);
  } catch (e) {
    console.error('Upload failed', e);
  } finally {
    docUploading.value = false;
  }
}

async function deleteDocument(docId) {
  if (!selectedContactCard.value) return;
  try {
    await pipelineItemDocumentsApi.delete(
      selectedContactCard.value.pipeline_id,
      selectedContactCard.value.id,
      docId
    );
    itemDocuments.value = itemDocuments.value.filter(d => d.id !== docId);
  } catch (e) {
    console.error('Delete failed', e);
  }
}

function onDocFileChange(e) {
  newDocFile.value = e.target.files?.[0] || null;
}

function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

watch(modalActiveTab, tab => {
  if (tab === 'documents' && selectedContactCard.value) {
    loadDocuments(selectedContactCard.value.pipeline_id, selectedContactCard.value.id);
  }
});

// ─── Filled custom attributes count for "Dados" tab ───
const filledCustomAttributes = computed(() => {
  const attrs = selectedContact.value?.custom_attributes || {};
  return Object.entries(attrs).filter(([, v]) => v !== null && v !== undefined && v !== '');
});
const filledCustomAttributesCount = computed(() => filledCustomAttributes.value.length);

// ─── "Dados" tab: attribute grouping & labels ───
const companyAttrKeys = ['nome_empresa', 'nome_fantasia', 'cnpj', 'porte_empresa', 'natureza_juridica', 'segmento', 'ramo_atuacao', 'cnae_codigo', 'cnae_descricao', 'cnaes_secundarios', 'socios', 'data_abertura'];
const locationAttrKeys = ['cidade', 'estado', 'endereco', 'bairro', 'cep', 'ddd'];
const financialAttrKeys = ['capital_social', 'valor_implementacao', 'valor_mensalidade', 'data_mensalidade', 'data_inicio_contrato', 'encerramento_contrato'];

const attrLabelMap = {
  nome_empresa: 'Empresa', nome_fantasia: 'Nome Fantasia', cnpj: 'CNPJ',
  porte_empresa: 'Porte', natureza_juridica: 'Nat. Juridica', segmento: 'Segmento',
  ramo_atuacao: 'Ramo', cnae_codigo: 'CNAE', cnae_descricao: 'Atividade Principal',
  cnaes_secundarios: 'CNAEs Secundarios', socios: 'Socios', data_abertura: 'Abertura',
  cidade: 'Cidade', estado: 'UF', endereco: 'Endereco', bairro: 'Bairro', cep: 'CEP', ddd: 'DDD',
  capital_social: 'Capital Social', valor_implementacao: 'Implementacao (R$)', valor_mensalidade: 'Mensalidade (R$)',
  data_mensalidade: 'Data Mensalidade', data_inicio_contrato: 'Inicio Contrato', encerramento_contrato: 'Encerr. Contrato',
  email_empresa: 'Email', telefone_empresa: 'Telefone', telefone_cliente: 'Tel. Cliente', telefone_agente: 'Tel. Agente',
  telefone_cobranca: 'Tel. Cobranca', link_grupo: 'Grupo WhatsApp', logo_empresa: 'Logo',
  clientName: 'Nome Cliente', historico: 'Historico', termometro: 'Termometro', origem: 'Origem',
  status_lead: 'Status Lead', status_enriquecimento: 'Enriquecimento', fontes_dados: 'Fontes',
  sky_status: 'Status', sky_description: 'Observacoes', tipo_agente: 'Tipo Agente',
  entregaveis: 'Entregaveis', resumo_reuniao: 'Resumo Reuniao',
  negociacao_implementacao: 'Neg. Implementacao', negociacao_mensalidade: 'Neg. Mensalidade',
  informacoes_adicionais_contrato: 'Info Contrato',
};

function attrLabel(key) {
  return attrLabelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatAttrValue(key, val) {
  if (val === null || val === undefined) return '-';
  if (key === 'capital_social') {
    const num = parseFloat(val);
    if (!isNaN(num)) return `R$ ${num.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  }
  if (key === 'cnpj' && String(val).length === 14) {
    return val.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }
  return String(val);
}

function hasAttrGroup(group) {
  const keys = filledCustomAttributes.value.map(([k]) => k);
  if (group === 'company') return keys.some(k => companyAttrKeys.includes(k));
  if (group === 'location') return keys.some(k => locationAttrKeys.includes(k));
  if (group === 'financial') return keys.some(k => financialAttrKeys.includes(k));
  if (group === 'other') return keys.some(k => !companyAttrKeys.includes(k) && !locationAttrKeys.includes(k) && !financialAttrKeys.includes(k));
  return false;
}

// ─── Feature 6: Conversations grouped by inbox ───
const conversationsByInbox = computed(() => {
  if (!contactConversations.value.length) return [];
  const groups = {};
  contactConversations.value.forEach(conv => {
    const inboxId = conv.inbox_id || 0;
    const channelType = conv.meta?.channel || '';
    const inboxObj = inboxes.value?.find(i => i.id === inboxId);
    const inboxName = inboxObj?.name || channelType.replace('Channel::', '') || `Inbox #${inboxId}`;
    if (!groups[inboxId]) {
      groups[inboxId] = { inbox_id: inboxId, inbox_name: inboxName, conversations: [] };
    }
    groups[inboxId].conversations.push(conv);
  });
  return Object.values(groups);
});

const stageCount = computed(() => boardStages.value.length);
const cardCount = computed(() =>
  boardStages.value.reduce((sum, stage) => sum + (stage.items?.length || 0), 0)
);
const selectedConversation = computed(() =>
  contactConversations.value.find(
    conversation => conversation.id === selectedConversationId.value
  )
);
const isPipelineManagerView = computed(
  () => hasPipelines.value && !selectedPipelineId.value
);
const managerPipelines = computed(() => {
  const query = pipelineManagerQuery.value.trim().toLowerCase();
  const filtered = pipelines.value.filter(pipeline => {
    if (!query) return true;
    return (
      pipeline.name?.toLowerCase().includes(query) ||
      pipeline.description?.toLowerCase().includes(query)
    );
  });

  const sorted = [...filtered];
  if (pipelineManagerSort.value === 'name_asc') {
    sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return sorted;
  }
  if (pipelineManagerSort.value === 'name_desc') {
    sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    return sorted;
  }
  if (pipelineManagerSort.value === 'position_asc') {
    sorted.sort((a, b) => (a.position || 0) - (b.position || 0));
    return sorted;
  }
  sorted.sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0));
  return sorted;
});
const pipelinesByFolder = computed(() => {
  const groups = {};
  managerPipelines.value.forEach(pipeline => {
    const folder = pipeline.folder?.trim() || '';
    if (!groups[folder]) groups[folder] = [];
    groups[folder].push(pipeline);
  });
  // Sort pipelines alphabetically within each folder
  Object.values(groups).forEach(list => {
    list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
  });
  const folderNames = Object.keys(groups).sort((a, b) => {
    if (!a) return 1;
    if (!b) return -1;
    return a.localeCompare(b, 'pt-BR');
  });
  return folderNames.map(name => ({ name, pipelines: groups[name] }));
});

const stageOrderChanged = computed(() => {
  if (!boardStages.value.length || !stageSettings.value.length) return false;
  const boardOrder = boardStages.value.map(stage => stage.id).join(',');
  const settingsOrder = stageSettings.value.map(stage => stage.id).join(',');
  return boardOrder !== settingsOrder;
});

const stageAccentClasses = [
  'border-t-n-blue-8',
  'border-t-n-teal-8',
  'border-t-n-amber-8',
  'border-t-n-ruby-8',
];

const resetPipelineForm = () => {
  pipelineForm.name = '';
  pipelineForm.description = '';
  pipelineForm.color = '';
  pipelineForm.icon = '';
};

const resetStageForm = () => {
  stageForm.name = '';
  stageForm.color = '';
};

const randomStageColor = () => {
  const palette = [
    '#2563EB',
    '#0F766E',
    '#D97706',
    '#DC2626',
    '#7C3AED',
    '#0891B2',
    '#65A30D',
    '#DB2777',
  ];
  return palette[Math.floor(Math.random() * palette.length)];
};

const loadPipelines = async () => {
  isLoading.value = true;
  try {
    const response = await PipelinesApi.list();
    pipelines.value = response.data.payload || [];
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.LOAD_PIPELINES'));
  } finally {
    isLoading.value = false;
  }
};

const loadBoard = async () => {
  if (!selectedPipelineId.value) return;

  isLoading.value = true;
  try {
    const response = await PipelinesApi.getItems(selectedPipelineId.value, {
      item_type: 'Contact',
      inbox_id: filters.inbox_id || undefined,
      q: filters.q || undefined,
      moved_from: filters.moved_from || undefined,
      moved_to: filters.moved_to || undefined,
      return_date_filter: filters.return_date_filter || undefined,
      sort_by: 'last_activity',
    });

    boardStages.value = response.data.stages || [];
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.LOAD_ITEMS'));
  } finally {
    isLoading.value = false;
  }
};

const openCreatePipelineDialog = () => {
  resetPipelineForm();
  createPipelineDialogRef.value?.open();
};

const markPipelineAsRecent = pipelineId => {
  lastOpenedPipelineId.value = pipelineId;
  if (!accountId.value || !pipelineId) return;
  window.localStorage.setItem(
    `kanban:lastPipeline:${accountId.value}`,
    String(pipelineId)
  );
};

const selectPipeline = pipelineId => {
  selectedPipelineId.value = pipelineId;
  markPipelineAsRecent(pipelineId);
};

const backToPipelineManager = () => {
  selectedPipelineId.value = null;
  boardStages.value = [];
};

const createPipeline = async () => {
  isSaving.value = true;
  try {
    const response = await PipelinesApi.create({
      name: pipelineForm.name,
      description: pipelineForm.description || undefined,
      color: pipelineForm.color || undefined,
      icon: pipelineForm.icon || undefined,
    });
    await loadPipelines();
    selectedPipelineId.value = response.data.id;
    markPipelineAsRecent(response.data.id);
    await loadBoard();
    createPipelineDialogRef.value?.close();
    useAlert(t('KANBAN_VIEW.SUCCESS.PIPELINE_CREATED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.PIPELINE_CREATE'));
  } finally {
    isSaving.value = false;
  }
};

const openCreateStageDialog = () => {
  resetStageForm();
  createStageDialogRef.value?.open();
};

const openPipelineSettingsDialog = async () => {
  if (!selectedPipelineId.value) return;
  isPipelineSettingsOpen.value = true;
  pipelineSettingsForm.name = selectedPipeline.value?.name || '';
  pipelineSettingsForm.description = selectedPipeline.value?.description || '';
  pipelineSettingsForm.color = selectedPipeline.value?.color || '';
  pipelineSettingsForm.icon = selectedPipeline.value?.icon || '';
  pipelineSettingsForm.card_display_attributes = JSON.parse(
    JSON.stringify(selectedPipeline.value?.card_display_attributes || [])
  );
  const savedSettings = selectedPipeline.value?.settings || {};
  pipelineSettingsForm.settings.alert_enabled = savedSettings.alert_enabled || false;
  pipelineSettingsForm.settings.alert_whatsapp_inbox_id = savedSettings.alert_whatsapp_inbox_id || '';
  pipelineSettingsForm.settings.alert_whatsapp_numbers_raw = (savedSettings.alert_whatsapp_numbers || []).join(', ');
  stageSettings.value = boardStages.value.map(stage => {
    const previousStageSetting = stageSettings.value.find(
      setting => setting.id === stage.id
    );
    return {
      id: stage.id,
      name: stage.name,
      color: stage.color || '',
      is_won: stage.is_won || false,
      triggers_conversion: stage.triggers_conversion || false,
      conversion_records_value: stage.conversion_records_value !== false,
      conversion_type_id: stage.conversion_type_id || '',
      card_count: stage.items?.length || 0,
      isSaving: previousStageSetting?.isSaving || false,
      stage_automations: JSON.parse(JSON.stringify(stage.stage_automations || [])),
      entry_requirements: JSON.parse(JSON.stringify(
        stage.entry_requirements || { enabled: false, require_all: true, attributes: [] }
      )),
    };
  });

  isLoadingAgents.value = true;
  try {
    const agentsRes = await PipelinesApi.getAgents(selectedPipelineId.value);
    const agentIds = (agentsRes.data?.agents || []).map(a => a.id);
    pipelineSettingsForm.restrictedAgents = agents.value.filter(a => agentIds.includes(a.id));
  } catch {
    pipelineSettingsForm.restrictedAgents = [];
  } finally {
    isLoadingAgents.value = false;
  }
};

const openPipelineSettingsFor = async pipeline => {
  selectPipeline(pipeline.id);
  await loadBoard();
  openPipelineSettingsDialog();
};

const closePipelineSettingsDialog = () => {
  isPipelineSettingsOpen.value = false;
};

const updatePipelineSettings = async () => {
  if (!selectedPipelineId.value) return;

  isSaving.value = true;
  try {
    await PipelinesApi.update(selectedPipelineId.value, {
      name: pipelineSettingsForm.name,
      description: pipelineSettingsForm.description || undefined,
      color: pipelineSettingsForm.color || undefined,
      icon: pipelineSettingsForm.icon || undefined,
      card_display_attributes: pipelineSettingsForm.card_display_attributes,
      settings: {
        alert_enabled: pipelineSettingsForm.settings.alert_enabled,
        alert_whatsapp_inbox_id: pipelineSettingsForm.settings.alert_whatsapp_inbox_id || null,
        alert_whatsapp_numbers: (pipelineSettingsForm.settings.alert_whatsapp_numbers_raw || '')
          .split(',').map(n => n.trim()).filter(Boolean),
      },
    });
    await PipelinesApi.setAgents(
      selectedPipelineId.value,
      pipelineSettingsForm.restrictedAgents.map(a => a.id)
    );
    await loadPipelines();
    await loadBoard();
    useAlert(t('KANBAN_VIEW.SUCCESS.PIPELINE_UPDATED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.PIPELINE_UPDATE'));
  } finally {
    isSaving.value = false;
  }
};

const toggleFolderCollapse = folderName => {
  const key = folderName || '__uncategorized__';
  if (collapsedFolders.value.has(key)) {
    collapsedFolders.value.delete(key);
  } else {
    collapsedFolders.value.add(key);
  }
};

const isFolderCollapsed = folderName => {
  return collapsedFolders.value.has(folderName || '__uncategorized__');
};

const movePipelineToFolder = async (pipeline, folderName) => {
  try {
    await PipelinesApi.update(pipeline.id, { folder: folderName || null });
    const found = pipelines.value.find(p => p.id === pipeline.id);
    if (found) found.folder = folderName || null;
    // Folder now has a real pipeline — remove from virtual list
    if (folderName) {
      virtualFolders.value = virtualFolders.value.filter(f => f !== folderName);
    }
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.PIPELINE_UPDATE'));
  }
};

const handleFolderDrop = async (event, targetFolder) => {
  const { added } = event;
  if (!added) return;
  await movePipelineToFolder(added.element, targetFolder);
};

const startRenameFolder = folderName => {
  editingFolder.value = folderName;
  editingFolderValue.value = folderName;
};

const confirmRenameFolder = async () => {
  const oldName = editingFolder.value;
  const newName = editingFolderValue.value.trim();
  if (!newName || newName === oldName) {
    editingFolder.value = null;
    return;
  }
  const toUpdate = pipelines.value.filter(p => (p.folder || '') === (oldName || ''));
  await Promise.all(toUpdate.map(p => PipelinesApi.update(p.id, { folder: newName })));
  toUpdate.forEach(p => { p.folder = newName; });
  editingFolder.value = null;
};

const deleteFolderGroup = async folderName => {
  const toUpdate = pipelines.value.filter(p => (p.folder || '') === (folderName || ''));
  await Promise.all(toUpdate.map(p => PipelinesApi.update(p.id, { folder: null })));
  toUpdate.forEach(p => { p.folder = null; });
};

const virtualFoldersKey = computed(() => `kanban:virtualFolders:${accountId.value}`);

const virtualFolders = ref(
  (() => {
    try {
      const key = `kanban:virtualFolders:${window.__currentAccountId || ''}`;
      return JSON.parse(window.localStorage.getItem(key) || '[]');
    } catch {
      return [];
    }
  })()
);

watch(virtualFolders, val => {
  if (accountId.value) {
    window.localStorage.setItem(virtualFoldersKey.value, JSON.stringify(val));
  }
}, { deep: true });

const createFolder = async () => {
  const name = newFolderName.value.trim();
  if (!name) return;
  newFolderName.value = '';
  showNewFolderInput.value = false;
  if (!pipelinesByFolder.value.find(g => g.name === name)) {
    collapsedFolders.value.delete(name);
    virtualFolders.value = [...virtualFolders.value.filter(f => f !== name), name];
  }
};

const allFolderGroups = computed(() => {
  const groups = [...pipelinesByFolder.value];
  virtualFolders.value.forEach(vf => {
    if (!groups.find(g => g.name === vf)) {
      groups.push({ name: vf, pipelines: [] });
    }
  });
  groups.sort((a, b) => {
    if (!a.name) return 1;
    if (!b.name) return -1;
    return a.name.localeCompare(b.name);
  });
  return groups;
});

const isCloning = ref(false);

const clonePipeline = async pipeline => {
  isCloning.value = pipeline.id;
  try {
    await PipelinesApi.clone(pipeline.id);
    await loadPipelines();
    useAlert(t('KANBAN_VIEW.SUCCESS.PIPELINE_CLONED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.PIPELINE_CLONE'));
  } finally {
    isCloning.value = false;
  }
};

const requestDeletePipeline = () => {
  deletePipelineDialogRef.value?.open();
};

const deletePipeline = async () => {
  if (!selectedPipelineId.value) return;

  isSaving.value = true;
  try {
    await PipelinesApi.delete(selectedPipelineId.value);
    selectedPipelineId.value = null;
    deletePipelineDialogRef.value?.close();
    closePipelineSettingsDialog();
    await loadPipelines();
    await loadBoard();
    useAlert(t('KANBAN_VIEW.SUCCESS.PIPELINE_REMOVED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.PIPELINE_REMOVE'));
  } finally {
    isSaving.value = false;
  }
};

const updateStageSettings = async stage => {
  stage.isSaving = true;
  try {
    await PipelinesApi.updateStage(stage.id, {
      name: stage.name,
      color: stage.color || undefined,
      triggers_conversion: stage.triggers_conversion,
      conversion_records_value: stage.conversion_records_value,
      conversion_type_id: stage.conversion_type_id || null,
      stage_automations: stage.stage_automations || [],
      entry_requirements: stage.entry_requirements || {},
    });
    await loadBoard();
    stage.isSaving = false;
    openPipelineSettingsDialog();
    useAlert(t('KANBAN_VIEW.SUCCESS.STAGE_UPDATED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.STAGE_UPDATE'));
    stage.isSaving = false;
  }
};

const requestDeleteStage = stage => {
  stagePendingDelete.value = stage;
  deleteStageDialogRef.value?.open();
};

const deleteStage = async () => {
  const stage = stagePendingDelete.value;
  if (!stage) return;

  stage.isSaving = true;
  try {
    await PipelinesApi.deleteStage(stage.id);
    stageSettings.value = stageSettings.value.filter(
      item => item.id !== stage.id
    );
    await loadBoard();
    useAlert(t('KANBAN_VIEW.SUCCESS.STAGE_REMOVED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.STAGE_REMOVE'));
  } finally {
    stage.isSaving = false;
    deleteStageDialogRef.value?.close();
    stagePendingDelete.value = null;
  }
};

const createStage = async () => {
  if (!selectedPipelineId.value) return;

  isSaving.value = true;
  try {
    await PipelinesApi.createStage(selectedPipelineId.value, {
      name: stageForm.name,
      color: stageForm.color || randomStageColor(),
    });
    await loadBoard();
    if (isPipelineSettingsOpen.value) {
      openPipelineSettingsDialog();
    }
    createStageDialogRef.value?.close();
    useAlert(t('KANBAN_VIEW.SUCCESS.STAGE_CREATED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.STAGE_CREATE'));
  } finally {
    isSaving.value = false;
  }
};

const duplicateStage = async stage => {
  if (!selectedPipelineId.value) return;

  stage.isSaving = true;
  try {
    await PipelinesApi.createStage(selectedPipelineId.value, {
      name: `${stage.name} ${t('KANBAN_VIEW.DIALOGS.PIPELINE_SETTINGS.COPY')}`,
      color: stage.color || randomStageColor(),
    });
    await loadBoard();
    openPipelineSettingsDialog();
    useAlert(t('KANBAN_VIEW.SUCCESS.STAGE_CREATED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.STAGE_CREATE'));
  } finally {
    stage.isSaving = false;
  }
};

const saveStageOrder = async () => {
  if (!selectedPipelineId.value || !stageOrderChanged.value) return;
  isSaving.value = true;
  try {
    await PipelinesApi.reorderStages(
      selectedPipelineId.value,
      stageSettings.value.map(stage => stage.id)
    );
    await loadBoard();
    openPipelineSettingsDialog();
    useAlert(t('KANBAN_VIEW.SUCCESS.STAGE_ORDER_SAVED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.STAGE_REORDER'));
  } finally {
    isSaving.value = false;
  }
};

const onStagesReordered = async () => {
  await saveStageOrder();
};

const loadAddCardCandidates = async () => {
  if (!selectedPipelineId.value || !isAddCardDialogOpen.value) return;

  const searchQuery = addCardSearch.value.trim();
  const response = searchQuery
    ? await ContactAPI.search(searchQuery, 1)
    : await ContactAPI.get(1);
  addCardCandidates.value = response.data.payload || [];
};

const debouncedLoadAddCardCandidates = debounce(async () => {
  try {
    await loadAddCardCandidates();
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.LOAD_CANDIDATES'));
  }
}, 300);

const openAddCardDialog = (stageId = null) => {
  targetStageId.value = stageId;
  addCardSearch.value = '';
  addCardCandidates.value = [];
  isAddCardDialogOpen.value = true;
  addCardDialogRef.value?.open();
  debouncedLoadAddCardCandidates();
};

const closeAddCardDialog = () => {
  isAddCardDialogOpen.value = false;
  targetStageId.value = null;
};

const addCardToPipeline = async candidate => {
  if (!selectedPipelineId.value) return;

  isAddingCard.value = true;
  try {
    await PipelinesApi.addItem(selectedPipelineId.value, {
      item_type: 'Contact',
      item_id: candidate.id,
      pipeline_stage_id: targetStageId.value || undefined,
    });
    await loadBoard();
    useAlert(t('KANBAN_VIEW.SUCCESS.ITEM_ADDED'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('KANBAN_VIEW.ERRORS.ITEM_ADD'));
  } finally {
    isAddingCard.value = false;
  }
};

const updateStageOrder = async () => {
  if (!isAdmin.value || !selectedPipelineId.value) return;

  try {
    await PipelinesApi.reorderStages(
      selectedPipelineId.value,
      boardStages.value.map(stage => stage.id)
    );
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.STAGE_REORDER'));
    await loadBoard();
  }
};

const removeItem = async item => {
  try {
    await PipelinesApi.removeItem(selectedPipelineId.value, item.id);
    await loadBoard();
    if (isPipelineSettingsOpen.value) {
      openPipelineSettingsDialog();
    }
    useAlert(t('KANBAN_VIEW.SUCCESS.ITEM_REMOVED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.ITEM_REMOVE'));
  }
};

const restartJourney = async card => {
  try {
    await PipelinesApi.restartJourney(selectedPipelineId.value, card.id);
    await loadBoard();
    useAlert(t('KANBAN_VIEW.SUCCESS.JOURNEY_RESTARTED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.JOURNEY_RESTART'));
  }
};

// ---- Attribute Automations helpers ----
const generateRuleId = () =>
  `rule_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const addAutomationRule = stage => {
  if (!stage.stage_automations) stage.stage_automations = [];
  stage.stage_automations.push({
    id: generateRuleId(),
    attribute_key: '',
    attribute_model: 'contact_attribute',
    condition: 'filled',
    target_stage_id: '',
  });
};

const removeAutomationRule = (stage, ruleId) => {
  stage.stage_automations = stage.stage_automations.filter(r => r.id !== ruleId);
};

const addMoveToPipelineRule = stage => {
  if (!stage.stage_automations) stage.stage_automations = [];
  stage.stage_automations.push({
    id: generateRuleId(),
    action: 'move_to_pipeline',
    label: 'Mover para Pos-Vendas',
    target_pipeline_id: '',
    target_stage_id: '',
  });
};

const pipelineNameById = id => {
  const p = pipelines.value.find(pp => pp.id === Number(id));
  return p ? p.name : `#${id}`;
};

const onMoveToPipelineChanged = rule => {
  const targetPipeline = pipelines.value.find(
    p => p.id === Number(rule.target_pipeline_id)
  );
  if (targetPipeline) {
    rule.label = `Mover para ${targetPipeline.name}`;
    rule.target_stage_id = '';
  }
};

const addEntryRequirementAttr = stage => {
  if (!stage.entry_requirements.attributes) stage.entry_requirements.attributes = [];
  stage.entry_requirements.attributes.push({ key: '', model: 'contact_attribute' });
};

const removeEntryRequirementAttr = (stage, idx) => {
  stage.entry_requirements.attributes.splice(idx, 1);
};

// ---- Card Display Attributes helpers ----
const addCardDisplayAttr = () => {
  pipelineSettingsForm.card_display_attributes.push({
    attribute_key: '',
    attribute_model: 'contact_attribute',
    label: '',
  });
};

const removeCardDisplayAttr = idx => {
  pipelineSettingsForm.card_display_attributes.splice(idx, 1);
};

const onCardDisplayAttrChange = (attr, attrKey) => {
  const found = allCustomAttributes.value.find(a => a.attribute_key === attrKey);
  if (found) attr.label = found.attribute_display_name;
};

const handleCardChange = async (toStage, event) => {
  const movedItem = event.added?.element || event.moved?.element;
  const targetPosition = event.added?.newIndex ?? event.moved?.newIndex;

  if (!movedItem) return;

  try {
    await PipelinesApi.moveItem(selectedPipelineId.value, movedItem.id, {
      to_stage_id: toStage.id,
      position: targetPosition,
    });
  } catch (error) {
    const data = error?.response?.data;
    if (data?.error === 'entry_requirement_failed') {
      gateErrorMissingAttrs.value = data.missing_attributes || [];
      gateErrorDialog.value?.open();
    } else {
      useAlert(data?.error || t('KANBAN_VIEW.ERRORS.ITEM_MOVE'));
    }
    await loadBoard();
  }
};

const loadMore = async stage => {
  if (!selectedPipelineId.value) return;

  try {
    const response = await PipelinesApi.getItems(selectedPipelineId.value, {
      item_type: 'Contact',
      inbox_id: filters.inbox_id || undefined,
      q: filters.q || undefined,
      moved_from: filters.moved_from || undefined,
      moved_to: filters.moved_to || undefined,
      return_date_filter: filters.return_date_filter || undefined,
      sort_by: 'last_activity',
      stage_offsets: {
        [stage.id]: stage.items.length,
      },
    });

    const updatedStage = (response.data.stages || []).find(
      stageItem => stageItem.id === stage.id
    );
    if (!updatedStage) return;

    const stageIndex = boardStages.value.findIndex(
      stageItem => stageItem.id === stage.id
    );
    if (stageIndex < 0) return;

    boardStages.value[stageIndex] = {
      ...boardStages.value[stageIndex],
      items: [...boardStages.value[stageIndex].items, ...updatedStage.items],
      meta: updatedStage.meta,
    };
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.LOAD_MORE'));
  }
};

const stageAccentClass = (stage, index) => {
  if (stage.is_won) return 'border-t-n-teal-9';
  if (stage.is_lost) return 'border-t-n-ruby-9';
  return stageAccentClasses[index % stageAccentClasses.length];
};

const contactInitials = card => {
  const name = card?.item?.name || '';
  const [first = '', second = ''] = name.split(' ');
  return `${first[0] || ''}${second[0] || ''}`.toUpperCase() || 'NA';
};

const formatTimestamp = timestamp => {
  if (!timestamp) return '-';
  return new Date(Number(timestamp) * 1000).toLocaleString();
};

const hasDateFilter = computed(
  () => filters.moved_from || filters.moved_to || filters.return_date_filter
);

const isFiltersOpen = ref(false);
const hasActiveFilters = computed(
  () => !!filters.inbox_id || !!filters.q || !!filters.moved_from || !!filters.moved_to || !!filters.return_date_filter
);

const clearDateFilter = () => {
  filters.moved_from = '';
  filters.moved_to = '';
  filters.return_date_filter = '';
  loadBoard();
};

const formatCurrency = value => {
  const num = parseFloat(value) || 0;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(num);
};

const startEditDealValue = card => {
  editingDealCard.value = card.id;
  dealValueInput.value = String(parseFloat(card.deal_value) || 0);
};

const cancelEditDealValue = () => {
  editingDealCard.value = null;
};

const saveDealValue = async card => {
  if (editingDealCard.value !== card.id) return;

  const newValue = parseFloat(dealValueInput.value) || 0;
  const prevValue = parseFloat(card.deal_value) || 0;
  editingDealCard.value = null;

  if (newValue === prevValue) return;

  try {
    await PipelinesApi.updateItem(selectedPipelineId.value, card.id, {
      deal_value: newValue,
    });

    card.deal_value = newValue;

    const stage = boardStages.value.find(s => s.items.some(i => i.id === card.id));
    if (stage?.meta) {
      stage.meta.deal_value_total =
        (parseFloat(stage.meta.deal_value_total) || 0) - prevValue + newValue;
    }

    if (selectedContactCard.value?.id === card.id) {
      selectedContactCard.value.deal_value = newValue;
    }
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.DEAL_VALUE_UPDATE'));
    card.deal_value = prevValue;
  }
};

const activeProductDropdownCard = ref(null);
const activeStageAutomationDropdown = ref(null);
const activeStageConversionDropdown = ref(null);

const automationRules = ref([]);
const showEditAutomationModal = ref(false);
const automationToEdit = ref(null);

const loadAutomationRules = async () => {
  try {
    const res = await automationsApi.get();
    automationRules.value = res.data?.payload || res.data || [];
  } catch {
    // silencioso — indicador não crítico
  }
};

const openEditAutomationModal = rule => {
  automationToEdit.value = rule;
  showEditAutomationModal.value = true;
  activeStageAutomationDropdown.value = null;
};

const hideEditAutomationModal = () => {
  showEditAutomationModal.value = false;
  automationToEdit.value = null;
};

const submitEditedAutomation = async (payload, mode) => {
  try {
    const action = mode === 'edit' ? 'automations/update' : 'automations/create';
    await store.dispatch(action, payload);
    useAlert(t('AUTOMATION.EDIT.API.SUCCESS_MESSAGE'));
    loadAutomationRules();
    hideEditAutomationModal();
  } catch {
    useAlert(t('AUTOMATION.EDIT.API.ERROR_MESSAGE'));
  }
};

const automationsForStage = stageId => {
  return automationRules.value.filter(rule => {
    const inConditions = (rule.conditions || []).some(c => {
      if (c.attribute_key !== 'pipeline_stage_to_id') return false;
      return (c.values || []).some(v => {
        const id = typeof v === 'object' && v !== null ? v.id : v;
        return Number(id) === Number(stageId);
      });
    });
    const inActions = (rule.actions || []).some(a => {
      if (!['add_to_pipeline_stage', 'move_to_pipeline_stage'].includes(a.action_name)) return false;
      return (a.action_params || []).some(p => Number(p) === Number(stageId));
    });
    return inConditions || inActions;
  });
};

const saveProductSelection = async (card, productIds) => {
  if (!card) return;
  try {
    await PipelinesApi.updateItem(selectedPipelineId.value, card.id, {
      product_ids: productIds,
    });
    card.product_ids = productIds;
    card.products = products.value.filter(p => productIds.includes(p.id));
    if (selectedContactCard.value?.id === card.id) {
      selectedContactCard.value.product_ids = productIds;
      selectedContactCard.value.products = card.products;
    }
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.PIPELINE_UPDATE'));
  }
};

const toggleProduct = (card, productId) => {
  const current = card.product_ids || [];
  const newIds = current.includes(productId)
    ? current.filter(id => id !== productId)
    : [...current, productId];
  saveProductSelection(card, newIds);
};

const formatMessageTimestamp = timestamp => {
  if (!timestamp) return '-';
  return new Date(Number(timestamp) * 1000).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const selectConversation = conversationId => {
  selectedConversationId.value = conversationId;
};

const loadConversationMessages = async conversationId => {
  if (!conversationId) {
    conversationMessages.value = [];
    return;
  }

  isLoadingMessages.value = true;
  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
    });
    conversationMessages.value = response.data.payload || [];
  } catch {
    conversationMessages.value = [];
    useAlert(t('KANBAN_VIEW.ERRORS.LOAD_MESSAGES'));
  } finally {
    isLoadingMessages.value = false;
  }
};

// ─── Feature 1: Notes functions ───
const loadContactNotes = async contactId => {
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/contacts/${contactId}/notes`
    );
    contactNotes.value = response.data || [];
  } catch {
    contactNotes.value = [];
  }
};

const addItemNote = async () => {
  if (!newNoteContent.value.trim() || !selectedContactCard.value) return;
  isSavingNote.value = true;
  const note = {
    id: Date.now(),
    content: newNoteContent.value.trim(),
    created_at: Math.floor(Date.now() / 1000),
    created_by_name: store.getters['auth/getCurrentUser']?.name || 'Agent',
  };
  const currentNotes = selectedContactCard.value.metadata?.notes || [];
  const updatedNotes = [...currentNotes, note];
  const updatedMetadata = { ...(selectedContactCard.value.metadata || {}), notes: updatedNotes };
  try {
    await PipelinesApi.updateItem(selectedPipelineId.value, selectedContactCard.value.id, {
      metadata: updatedMetadata,
    });
    selectedContactCard.value.metadata = updatedMetadata;
    itemNotes.value = updatedNotes;
    newNoteContent.value = '';
    useAlert(t('KANBAN_VIEW.SUCCESS.NOTE_SAVED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.NOTE_SAVE'));
  } finally {
    isSavingNote.value = false;
  }
};

// ─── Feature 2: Funnel management functions ───
const loadItemMoves = async () => {
  if (!selectedContactCard.value) return;
  isLoadingMoves.value = true;
  try {
    const response = await PipelinesApi.getItemMoves(
      selectedPipelineId.value,
      selectedContactCard.value.id
    );
    itemMoves.value = response.data || [];
  } catch {
    itemMoves.value = [];
  } finally {
    isLoadingMoves.value = false;
  }
};

const stageNameById = stageId => {
  if (!stageId) return '-';
  return boardStages.value.find(s => s.id === stageId)?.name || `#${stageId}`;
};

const moveFromModal = async () => {
  if (!modalMoveTargetStageId.value || !selectedContactCard.value) return;
  isMovingFromModal.value = true;
  try {
    await PipelinesApi.moveItem(selectedPipelineId.value, selectedContactCard.value.id, {
      to_stage_id: modalMoveTargetStageId.value,
      position: 0,
      notes: modalMoveNotes.value || undefined,
    });
    selectedContactCard.value.pipeline_stage_id = modalMoveTargetStageId.value;
    modalMoveNotes.value = '';
    modalMoveTargetStageId.value = null;
    await Promise.all([loadBoard(), loadItemMoves()]);
    useAlert(t('KANBAN_VIEW.SUCCESS.STAGE_MOVED'));
  } catch (error) {
    const data = error?.response?.data;
    if (data?.error === 'entry_requirement_failed') {
      gateErrorMissingAttrs.value = data.missing_attributes || [];
      gateErrorDialog.value?.open();
    } else {
      useAlert(data?.error || t('KANBAN_VIEW.ERRORS.ITEM_MOVE'));
    }
  } finally {
    isMovingFromModal.value = false;
  }
};

// ─── Feature 3: Return date functions ───
const saveReturnDate = async () => {
  if (!selectedContactCard.value) return;
  isSavingReturnDate.value = true;
  try {
    const dateValue = returnDateInput.value || null;
    await PipelinesApi.updateItem(selectedPipelineId.value, selectedContactCard.value.id, {
      return_date: dateValue,
    });
    selectedContactCard.value.return_date = dateValue
      ? Math.floor(new Date(dateValue).getTime() / 1000)
      : null;
    useAlert(t('KANBAN_VIEW.SUCCESS.RETURN_DATE_SAVED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.RETURN_DATE_SAVE'));
  } finally {
    isSavingReturnDate.value = false;
  }
};

// ─── Feature 5: Tasks functions ───
const addTask = async () => {
  if (!newTaskTitle.value.trim() || !selectedContactCard.value) return;
  const task = {
    id: Date.now().toString(),
    title: newTaskTitle.value.trim(),
    done: false,
    due_date: newTaskDueDate.value || null,
    created_at: Math.floor(Date.now() / 1000),
    created_by_name: store.getters['auth/getCurrentUser']?.name || 'Agent',
  };
  const currentTasks = selectedContactCard.value.metadata?.tasks || [];
  const updatedTasks = [...currentTasks, task];
  const updatedMetadata = { ...(selectedContactCard.value.metadata || {}), tasks: updatedTasks };
  try {
    await PipelinesApi.updateItem(selectedPipelineId.value, selectedContactCard.value.id, {
      metadata: updatedMetadata,
    });
    selectedContactCard.value.metadata = updatedMetadata;
    newTaskTitle.value = '';
    newTaskDueDate.value = '';
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.TASK_SAVE'));
  }
};

const toggleTaskDone = async taskId => {
  const tasks = [...(selectedContactCard.value.metadata?.tasks || [])];
  const task = tasks.find(t => t.id === taskId);
  if (!task) return;
  task.done = !task.done;
  const updatedMetadata = { ...(selectedContactCard.value.metadata || {}), tasks };
  try {
    await PipelinesApi.updateItem(selectedPipelineId.value, selectedContactCard.value.id, {
      metadata: updatedMetadata,
    });
    selectedContactCard.value.metadata = updatedMetadata;
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.TASK_SAVE'));
  }
};

const removeTask = async taskId => {
  const tasks = (selectedContactCard.value.metadata?.tasks || []).filter(
    t => t.id !== taskId
  );
  const updatedMetadata = { ...(selectedContactCard.value.metadata || {}), tasks };
  try {
    await PipelinesApi.updateItem(selectedPipelineId.value, selectedContactCard.value.id, {
      metadata: updatedMetadata,
    });
    selectedContactCard.value.metadata = updatedMetadata;
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.TASK_SAVE'));
  }
};

// ─── Feature 7: Product custom values ───
const initProductValues = () => {
  productValues.value = { ...(selectedContactCard.value?.metadata?.product_values || {}) };
};

const saveProductValues = async () => {
  if (!selectedContactCard.value) return;
  const updatedMetadata = {
    ...(selectedContactCard.value.metadata || {}),
    product_values: productValues.value,
  };
  try {
    await PipelinesApi.updateItem(selectedPipelineId.value, selectedContactCard.value.id, {
      metadata: updatedMetadata,
      deal_value: computedDealValue.value,
    });
    selectedContactCard.value.metadata = updatedMetadata;
    selectedContactCard.value.deal_value = computedDealValue.value;
    // Update stage total
    const stage = boardStages.value.find(
      s => s.id === selectedContactCard.value.pipeline_stage_id
    );
    if (stage) await loadBoard();
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.PRODUCT_VALUES_SAVE'));
  }
};

const openConversationFromModal = conversationId => {
  if (!conversationId) return;

  router.push(
    accountScopedRoute('inbox_conversation', {
      conversation_id: conversationId,
    })
  );
};

const openScheduleDialogFromKanban = () => {
  scheduleDialogRef.value?.open();
};

const openCampaignShortcutForStage = stage => {
  if (!selectedPipeline.value || !stage?.id) return;

  router.push(
    accountScopedRoute(
      'campaigns_whatsapp_new',
      {},
      {
        audience_type: 'pipeline_stage',
        pipeline_id: selectedPipeline.value.id,
        stage_id: stage.id,
      }
    )
  );
};

const openAutomationDialogForStage = stage => {
  if (!selectedPipeline.value) return;

  automationPreset.value = {
    name: `${selectedPipeline.value.name} - ${stage.name}`,
    description: `Automation for ${selectedPipeline.value.name} > ${stage.name}`,
    event_name: 'conversation_moved_in_pipeline',
    execution_mode: 'immediate',
    execution_delay_minutes: 0,
    conditions: [
      {
        attribute_key: 'pipeline_id',
        filter_operator: 'equal_to',
        values: [
          {
            id: selectedPipeline.value.id,
            name: selectedPipeline.value.name,
          },
        ],
        query_operator: 'and',
        custom_attribute_type: '',
      },
      {
        attribute_key: 'pipeline_stage_to_id',
        filter_operator: 'equal_to',
        values: [
          {
            id: stage.id,
            name: `${selectedPipeline.value.name} / ${stage.name}`,
          },
        ],
        query_operator: 'and',
        custom_attribute_type: '',
      },
    ],
    actions: [
      {
        action_name: 'assign_agent',
        action_params: [],
      },
    ],
  };

  showAddAutomationModal.value = true;
};

const hideAddAutomationModal = () => {
  showAddAutomationModal.value = false;
  automationPreset.value = null;
};

const submitAutomationFromKanban = async payload => {
  try {
    await store.dispatch('automations/create', payload);
    useAlert(t('KANBAN_VIEW.SUCCESS.AUTOMATION_CREATED'));
    loadAutomationRules();
    hideAddAutomationModal();
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.AUTOMATION_CREATE'));
  }
};

const openContactDetails = async card => {
  selectedContactCard.value = card;
  selectedContact.value = card.item;
  contactConversations.value = [];
  selectedConversationId.value = null;
  conversationMessages.value = [];
  isContactDetailsOpen.value = true;
  isEditingContact.value = false;
  contactLabels.value = [];
  newContactLabel.value = '';

  const contactId = card.item_type === 'Conversation' ? card.item.contact_id : card.item.id;

  isLoadingContactDetails.value = true;
  try {
    const [contactResponse, conversationResponse, labelsResponse, freshItemResponse] =
      await Promise.all([
        ContactAPI.show(contactId),
        ContactAPI.getConversations(contactId),
        ContactAPI.getContactLabels(contactId),
        PipelinesApi.getItem(card.pipeline_id, card.id).catch(() => null),
      ]);

    selectedContact.value = contactResponse.data.payload || card.item;
    contactEditForm.name = selectedContact.value?.name || '';
    contactEditForm.email = selectedContact.value?.email || '';
    contactEditForm.phone_number = selectedContact.value?.phone_number || '';
    contactConversations.value = conversationResponse.data.payload || [];
    contactLabels.value = labelsResponse.data.payload || [];
    selectedConversationId.value = contactConversations.value[0]?.id || null;

    if (freshItemResponse?.data?.item) {
      selectedContactCard.value = { ...selectedContactCard.value, item: freshItemResponse.data.item };
      const stage = boardStages.value.find(s => s.id === card.pipeline_stage_id);
      if (stage) {
        const idx = stage.items.findIndex(i => i.id === card.id);
        if (idx !== -1) stage.items[idx] = selectedContactCard.value;
      }
    }

    await loadConversationMessages(selectedConversationId.value);

    // Load notes, moves, and init product values
    await Promise.all([loadContactNotes(contactId), loadItemMoves()]);
    itemNotes.value = selectedContactCard.value?.metadata?.notes || [];
    initProductValues();

    // Set return date input
    const rdTs = selectedContactCard.value?.return_date;
    returnDateInput.value = rdTs
      ? new Date(rdTs * 1000).toISOString().slice(0, 16)
      : '';
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.LOAD_CONTACT_DETAILS'));
  } finally {
    isLoadingContactDetails.value = false;
  }
};

const closeContactDetails = () => {
  isContactDetailsOpen.value = false;
};

const startEditContact = () => {
  isEditingContact.value = true;
};

const cancelEditContact = () => {
  isEditingContact.value = false;
  contactEditForm.name = selectedContact.value?.name || '';
  contactEditForm.email = selectedContact.value?.email || '';
  contactEditForm.phone_number = selectedContact.value?.phone_number || '';
};

const saveContactDetails = async () => {
  if (!selectedContact.value?.id) return;
  isSavingContact.value = true;
  try {
    const response = await ContactAPI.update(selectedContact.value.id, {
      name: contactEditForm.name,
      email: contactEditForm.email || undefined,
      phone_number: contactEditForm.phone_number || undefined,
    });
    selectedContact.value = response.data.payload || selectedContact.value;
    isEditingContact.value = false;
    await loadBoard();
    useAlert(t('KANBAN_VIEW.SUCCESS.CONTACT_UPDATED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.CONTACT_UPDATE'));
  } finally {
    isSavingContact.value = false;
  }
};

const addContactLabel = async () => {
  if (!selectedContact.value?.id || !newContactLabel.value.trim()) return;
  const nextLabels = [
    ...new Set([...contactLabels.value, newContactLabel.value.trim()]),
  ];
  isUpdatingLabels.value = true;
  try {
    await ContactAPI.updateContactLabels(selectedContact.value.id, nextLabels);
    contactLabels.value = nextLabels;
    newContactLabel.value = '';
    useAlert(t('KANBAN_VIEW.SUCCESS.CONTACT_LABEL_UPDATED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.CONTACT_LABEL_UPDATE'));
  } finally {
    isUpdatingLabels.value = false;
  }
};

const removeContactLabel = async label => {
  if (!selectedContact.value?.id) return;
  const nextLabels = contactLabels.value.filter(item => item !== label);
  isUpdatingLabels.value = true;
  try {
    await ContactAPI.updateContactLabels(selectedContact.value.id, nextLabels);
    contactLabels.value = nextLabels;
    useAlert(t('KANBAN_VIEW.SUCCESS.CONTACT_LABEL_UPDATED'));
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.CONTACT_LABEL_UPDATE'));
  } finally {
    isUpdatingLabels.value = false;
  }
};

onMounted(async () => {
  if (accountId.value) {
    const recentPipelineId = Number(
      window.localStorage.getItem(`kanban:lastPipeline:${accountId.value}`)
    );
    if (recentPipelineId) {
      lastOpenedPipelineId.value = recentPipelineId;
    }
    // Load persisted virtual folders for this account
    try {
      const saved = JSON.parse(window.localStorage.getItem(`kanban:virtualFolders:${accountId.value}`) || '[]');
      if (saved.length) virtualFolders.value = saved;
    } catch { /* noop */ }
  }
  // Load products eagerly (watch may miss if account store isn't ready yet)
  if (!products.value.length) {
    productsApi.list({ active_only: true })
      .then(r => { products.value = r.data?.payload || r.data || []; })
      .catch(() => {});
  }
  await Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('inboxes/get'),
    store.dispatch('labels/get'),
    store.dispatch('attributes/get'),
    conversionTypesApi.list().then(res => { conversionTypes.value = res.data?.payload || []; }).catch(() => {}),
  ]);
  await loadPipelines();
  await loadBoard();
  loadAutomationRules();
  emitter.on(BUS_EVENTS.PIPELINE_ITEM_MOVED, onRealtimePipelineItemMoved);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.PIPELINE_ITEM_MOVED, onRealtimePipelineItemMoved);
});

const onRealtimePipelineItemMoved = async ({ pipeline_id: pipelineId, pipeline_item_id: itemId, from_stage_id: fromStageId, to_stage_id: toStageId }) => {
  if (pipelineId !== selectedPipelineId.value) return;

  try {
    const res = await PipelinesApi.getItem(pipelineId, itemId);
    const freshItem = res.data;
    if (!freshItem) return;

    // Remove from source stage
    const fromStage = boardStages.value.find(s => s.id === fromStageId);
    if (fromStage) {
      fromStage.items = fromStage.items.filter(i => i.id !== itemId);
    }

    // Insert into target stage at position 0
    const toStage = boardStages.value.find(s => s.id === toStageId);
    if (toStage) {
      toStage.items = toStage.items.filter(i => i.id !== itemId);
      toStage.items.unshift(freshItem);
    }
  } catch {
    // silently ignore - board will be consistent on next reload
  }
};

watch(selectedPipelineId, async () => {
  await loadBoard();
});

watch(addCardSearch, () => {
  debouncedLoadAddCardCandidates();
});

watch(selectedConversationId, async conversationId => {
  await loadConversationMessages(conversationId);
});
</script>

<template>
  <FeatureLockedScreen v-if="!hasCrm" feature-key="crm" />
  <div
    v-else
    class="flex h-full flex-col overflow-hidden bg-gradient-to-br from-n-blue-2 via-n-alpha-1 to-n-teal-2"
  >
    <!-- Compact header bar -->
    <header class="flex flex-shrink-0 items-center gap-2 border-b border-n-alpha-3 bg-n-solid-1/95 px-4 py-2.5 backdrop-blur-sm">
      <div class="flex min-w-0 flex-1 items-center gap-3">
        <h1 class="whitespace-nowrap text-sm font-semibold text-n-slate-12">
          {{ t('KANBAN_VIEW.TITLE') }}
        </h1>
        <div v-if="selectedPipelineId" class="hidden items-center gap-1.5 text-xs text-n-slate-10 md:flex">
          <span class="rounded-md bg-n-alpha-2 px-1.5 py-0.5 text-n-slate-11">{{ pipelines.length }} {{ t('KANBAN_VIEW.STATS.PIPELINES') }}</span>
          <span>·</span>
          <span class="rounded-md bg-n-alpha-2 px-1.5 py-0.5 text-n-slate-11">{{ stageCount }} {{ t('KANBAN_VIEW.STATS.STAGES') }}</span>
          <span>·</span>
          <span class="rounded-md bg-n-alpha-2 px-1.5 py-0.5 text-n-slate-11">{{ cardCount }} {{ t('KANBAN_VIEW.STATS.CARDS') }}</span>
        </div>
      </div>
      <div class="flex flex-shrink-0 items-center gap-1.5">
        <button
          v-if="selectedPipelineId"
          type="button"
          class="relative flex items-center gap-1.5 rounded-lg border border-n-alpha-3 bg-n-background px-3 py-1.5 text-xs text-n-slate-11 transition-colors hover:bg-n-alpha-1"
          :class="{ 'border-n-blue-7 text-n-blue-11': isFiltersOpen }"
          @click="isFiltersOpen = !isFiltersOpen"
        >
          <span class="i-lucide-sliders-horizontal text-sm" />
          {{ t('KANBAN_VIEW.ACTIONS.FILTERS') }}
          <span v-if="hasActiveFilters" class="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-n-brand" />
        </button>
        <Button
          size="sm"
          variant="outline"
          color="slate"
          icon="i-lucide-columns-3"
          :label="t('KANBAN_VIEW.ACTIONS.CREATE_PIPELINE')"
          @click="openCreatePipelineDialog"
        />
        <Button
          v-if="isAdmin"
          size="sm"
          variant="outline"
          color="slate"
          icon="i-lucide-align-horizontal-space-between"
          :label="t('KANBAN_VIEW.ACTIONS.CREATE_STAGE')"
          :disabled="!selectedPipelineId"
          @click="openCreateStageDialog"
        />
        <Button
          v-if="isAdmin"
          size="sm"
          variant="outline"
          color="slate"
          icon="i-lucide-settings-2"
          :label="t('KANBAN_VIEW.ACTIONS.CONFIGURE_PIPELINE')"
          :disabled="!selectedPipelineId"
          @click="openPipelineSettingsDialog"
        />
        <Button
          v-if="selectedPipelineId"
          size="sm"
          variant="outline"
          color="slate"
          icon="i-lucide-arrow-left"
          :label="t('KANBAN_VIEW.ACTIONS.BACK_TO_PIPELINES')"
          @click="backToPipelineManager"
        />
        <Button
          size="sm"
          variant="outline"
          color="teal"
          icon="i-lucide-plus"
          label="Criar Contato"
          :disabled="!selectedPipelineId"
          @click="showQuickContactDialog = true"
        />
        <Button
          size="sm"
          color="teal"
          icon="i-lucide-user-plus"
          :label="t('KANBAN_VIEW.ACTIONS.ADD_CONTACT')"
          :disabled="!selectedPipelineId"
          @click="openAddCardDialog()"
        />
      </div>
    </header>

    <!-- Collapsible filter panel -->
    <div
      v-if="selectedPipelineId"
      v-show="isFiltersOpen"
      class="flex-shrink-0 border-b border-n-alpha-3 bg-n-solid-1/80 backdrop-blur-sm"
    >
      <div class="grid grid-cols-1 gap-2 px-4 py-3 md:grid-cols-5">
        <select
          v-model="filters.inbox_id"
          class="rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-sm text-n-slate-12"
          @change="loadBoard"
        >
          <option value="">{{ t('KANBAN_VIEW.FILTERS.ALL_INBOXES') }}</option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
        <select
          v-model="filters.return_date_filter"
          class="rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-sm text-n-slate-12"
          @change="loadBoard"
        >
          <option value="">Data de retorno</option>
          <option value="today">Retorno hoje</option>
          <option value="overdue">Retorno vencido</option>
          <option value="upcoming">Retorno proximo (7 dias)</option>
          <option value="has_return">Com data de retorno</option>
          <option value="no_return">Sem data de retorno</option>
        </select>
        <div class="md:col-span-3">
          <Input
            v-model="filters.q"
            :placeholder="t('KANBAN_VIEW.FILTERS.SEARCH')"
            custom-input-class="!mb-0"
            @enter="loadBoard"
          />
        </div>
        <div class="flex flex-wrap items-center gap-2 md:col-span-4">
          <span class="shrink-0 text-xs text-n-slate-10">
            {{ t('KANBAN_VIEW.FILTERS.MOVED_AT') }}
          </span>
          <input
            v-model="filters.moved_from"
            type="date"
            class="rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-blue-7"
            @change="loadBoard"
          />
          <span class="text-xs text-n-slate-10">
            {{ t('KANBAN_VIEW.FILTERS.MOVED_AT_TO') }}
          </span>
          <input
            v-model="filters.moved_to"
            type="date"
            class="rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-blue-7"
            @change="loadBoard"
          />
          <button
            v-if="hasDateFilter"
            type="button"
            class="flex items-center gap-1 rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-xs text-n-ruby-11 hover:border-n-ruby-7"
            @click="clearDateFilter"
          >
            <span class="i-lucide-x text-xs" />
            {{ t('KANBAN_VIEW.FILTERS.CLEAR_DATES') }}
          </button>
        </div>
      </div>
    </div>

    <p v-if="!hasPipelines" class="px-4 py-4 text-sm text-n-slate-11">
      {{ t('KANBAN_VIEW.EMPTY.PIPELINES') }}
    </p>

    <div v-else-if="isPipelineManagerView" class="flex-1 min-h-0 overflow-y-auto p-4">
    <section
      class="rounded-2xl border border-n-alpha-3 bg-n-solid-1/95 p-4"
    >
      <div class="mb-4 flex items-center justify-between gap-2">
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('KANBAN_VIEW.MANAGER.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ t('KANBAN_VIEW.MANAGER.DESCRIPTION') }}
          </p>
        </div>
        <Button
          size="sm"
          color="blue"
          icon="i-lucide-plus"
          :label="t('KANBAN_VIEW.ACTIONS.CREATE_PIPELINE')"
          @click="openCreatePipelineDialog"
        />
      </div>

      <div class="mb-4 grid grid-cols-1 gap-2 md:grid-cols-4">
        <Input
          v-model="pipelineManagerQuery"
          :placeholder="t('KANBAN_VIEW.MANAGER.SEARCH_PLACEHOLDER')"
          custom-input-class="!mb-0"
        />
        <select
          v-model="pipelineManagerSort"
          class="rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-sm text-n-slate-12"
        >
          <option value="updated_desc">
            {{ t('KANBAN_VIEW.MANAGER.SORT_UPDATED') }}
          </option>
          <option value="name_asc">
            {{ t('KANBAN_VIEW.MANAGER.SORT_NAME_ASC') }}
          </option>
          <option value="name_desc">
            {{ t('KANBAN_VIEW.MANAGER.SORT_NAME_DESC') }}
          </option>
          <option value="position_asc">
            {{ t('KANBAN_VIEW.MANAGER.SORT_POSITION') }}
          </option>
        </select>
        <div class="flex items-center gap-1 rounded-lg border border-n-alpha-3 bg-n-background p-1">
          <button
            class="flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition"
            :class="pipelineManagerViewMode === 'cards' ? 'bg-n-brand text-white shadow-sm' : 'text-n-slate-10 hover:bg-n-alpha-2'"
            @click="pipelineManagerViewMode = 'cards'"
          >
            <span class="i-lucide-layout-grid text-sm" />
            Cards
          </button>
          <button
            class="flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition"
            :class="pipelineManagerViewMode === 'list' ? 'bg-n-brand text-white shadow-sm' : 'text-n-slate-10 hover:bg-n-alpha-2'"
            @click="pipelineManagerViewMode = 'list'"
          >
            <span class="i-lucide-list text-sm" />
            Lista
          </button>
        </div>
        <div
          class="rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-sm text-n-slate-11"
        >
          {{ t('KANBAN_VIEW.MANAGER.BROWSE_HINT') }}
        </div>
      </div>

      <!-- LIST VIEW -->
      <div v-if="pipelineManagerViewMode === 'list'" class="space-y-3">
        <div
          v-for="group in allFolderGroups"
          :key="'list-' + (group.name || '__uncategorized__')"
          class="overflow-hidden rounded-xl border border-n-alpha-3 bg-n-alpha-1/50"
        >
          <button
            type="button"
            class="flex w-full items-center gap-2 border-b border-n-alpha-2 bg-n-surface-2 px-4 py-2.5 text-left transition hover:bg-n-alpha-2"
            @click="toggleFolderCollapse(group.name)"
          >
            <span
              class="h-4 w-4 text-n-slate-10 transition-transform duration-150"
              :class="isFolderCollapsed(group.name) ? 'i-lucide-chevron-right' : 'i-lucide-chevron-down'"
            />
            <span class="i-lucide-folder h-4 w-4 text-n-brand" />
            <span class="text-sm font-semibold text-n-slate-12">
              {{ group.name || t('KANBAN_VIEW.FOLDERS.UNCATEGORIZED') }}
            </span>
            <span class="rounded-full bg-n-alpha-2 px-1.5 py-0.5 text-xs text-n-slate-10">
              {{ group.pipelines.length }}
            </span>
          </button>
          <table
            v-if="!isFolderCollapsed(group.name) && group.pipelines.length > 0"
            class="w-full text-sm"
          >
            <thead>
              <tr class="border-b border-n-alpha-2 bg-n-surface-2/50">
                <th class="px-4 py-2 text-left font-medium text-n-slate-10">Nome</th>
                <th class="px-4 py-2 text-left font-medium text-n-slate-10">Pasta</th>
                <th class="hidden px-4 py-2 text-left font-medium text-n-slate-10 md:table-cell">ID</th>
                <th class="px-4 py-2 text-right font-medium text-n-slate-10">Acoes</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="pipeline in group.pipelines"
                :key="pipeline.id"
                class="border-b border-n-alpha-1 transition hover:bg-n-alpha-1 last:border-0"
              >
                <td class="px-4 py-2.5">
                  <div class="flex items-center gap-2">
                    <span
                      v-if="pipeline.color"
                      class="inline-block h-3 w-3 shrink-0 rounded-full"
                      :style="{ backgroundColor: pipeline.color }"
                    />
                    <span class="font-medium text-n-slate-12">{{ pipeline.name }}</span>
                    <span
                      v-if="pipeline.is_default"
                      class="rounded-full bg-n-teal-2 px-1.5 py-0.5 text-[10px] text-n-teal-11"
                    >
                      {{ t('KANBAN_VIEW.MANAGER.DEFAULT') }}
                    </span>
                    <span
                      v-if="pipeline.id === lastOpenedPipelineId"
                      class="rounded-full bg-n-blue-2 px-1.5 py-0.5 text-[10px] text-n-blue-11"
                    >
                      {{ t('KANBAN_VIEW.MANAGER.RECENT') }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-2.5 text-n-slate-10">
                  {{ pipeline.folder || '-' }}
                </td>
                <td class="hidden px-4 py-2.5 text-n-slate-10 md:table-cell">
                  #{{ pipeline.id }}
                </td>
                <td class="px-4 py-2.5 text-right">
                  <div class="flex items-center justify-end gap-1">
                    <Button
                      size="sm"
                      color="blue"
                      :label="t('KANBAN_VIEW.MANAGER.OPEN')"
                      @click="selectPipeline(pipeline.id)"
                    />
                    <Button
                      v-if="isAdmin"
                      size="sm"
                      color="slate"
                      variant="outline"
                      icon="i-lucide-settings"
                      @click="openPipelineSettingsFor(pipeline)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div
            v-else-if="!isFolderCollapsed(group.name) && group.pipelines.length === 0"
            class="flex items-center justify-center py-6 text-sm text-n-slate-10"
          >
            {{ t('KANBAN_VIEW.FOLDERS.DROP_HERE') }}
          </div>
        </div>
      </div>

      <!-- CARDS VIEW (Folder groups) -->
      <div v-else class="space-y-3">
        <div
          v-for="group in allFolderGroups"
          :key="group.name || '__uncategorized__'"
          class="rounded-xl border border-n-alpha-3 bg-n-alpha-1/50"
        >
          <!-- Folder header -->
          <div
            class="flex items-center gap-2 px-3 py-2"
          >
            <button
              type="button"
              class="flex flex-1 items-center gap-2 text-left"
              @click="toggleFolderCollapse(group.name)"
            >
              <span class="i-lucide-chevron-right h-4 w-4 text-n-slate-10 transition-transform duration-150"
                :class="{ 'rotate-90': !isFolderCollapsed(group.name) }"
              />
              <span class="i-lucide-folder h-4 w-4 text-n-brand" />
              <template v-if="editingFolder === group.name && group.name">
                <input
                  v-model="editingFolderValue"
                  class="rounded border border-n-alpha-4 bg-n-solid-1 px-2 py-0.5 text-sm font-semibold text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                  @click.stop
                  @keyup.enter="confirmRenameFolder"
                  @keyup.escape="editingFolder = null"
                  @blur="confirmRenameFolder"
                />
              </template>
              <span v-else class="text-sm font-semibold text-n-slate-12">
                {{ group.name || t('KANBAN_VIEW.FOLDERS.UNCATEGORIZED') }}
              </span>
              <span class="rounded-full bg-n-alpha-2 px-1.5 py-0.5 text-xs text-n-slate-10">
                {{ group.pipelines.length }}
              </span>
            </button>
            <div v-if="isAdmin && group.name" class="flex items-center gap-1">
              <button
                type="button"
                class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
                :title="t('KANBAN_VIEW.FOLDERS.RENAME')"
                @click="startRenameFolder(group.name)"
              >
                <span class="i-lucide-pencil h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                class="rounded p-1 text-n-slate-10 hover:bg-n-red-2 hover:text-n-red-11"
                :title="t('KANBAN_VIEW.FOLDERS.DELETE')"
                @click="deleteFolderGroup(group.name)"
              >
                <span class="i-lucide-trash-2 h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <!-- Pipeline cards grid (draggable) -->
          <div class="relative">
            <Draggable
              v-show="!isFolderCollapsed(group.name)"
              :list="group.pipelines"
              group="pipeline-folders"
              item-key="id"
              class="grid min-h-[5rem] grid-cols-1 gap-3 p-3 pt-0 md:grid-cols-2 xl:grid-cols-3"
              ghost-class="opacity-40"
              :emptyInsertThreshold="60"
              @change="handleFolderDrop($event, group.name)"
            >
              <template #item="{ element: pipeline }">
                <article
                  class="cursor-grab rounded-xl border border-n-alpha-3 bg-gradient-to-br from-n-solid-1 to-n-alpha-1 p-4 shadow-sm active:cursor-grabbing"
                  :style="pipeline.color ? { borderTop: `4px solid ${pipeline.color}` } : {}"
                >
                  <div class="flex items-center justify-between gap-2">
                    <p class="truncate text-base font-semibold text-n-slate-12">
                      {{ pipeline.name }}
                    </p>
                    <span
                      v-if="pipeline.id === lastOpenedPipelineId"
                      class="rounded-full bg-n-blue-2 px-2 py-0.5 text-xs font-medium text-n-blue-11"
                    >
                      {{ t('KANBAN_VIEW.MANAGER.RECENT') }}
                    </span>
                  </div>
                  <p class="mt-1 line-clamp-2 text-sm text-n-slate-11">
                    {{ pipeline.description || '-' }}
                  </p>
                  <div class="mt-3 flex flex-wrap gap-1">
                    <span
                      v-if="pipeline.is_default"
                      class="rounded-full bg-n-teal-2 px-2 py-0.5 text-xs text-n-teal-11"
                    >
                      {{ t('KANBAN_VIEW.MANAGER.DEFAULT') }}
                    </span>
                    <span class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11">
                      {{ t('KANBAN_VIEW.MANAGER.PIPELINE_ID', { id: pipeline.id }) }}
                    </span>
                  </div>
                  <div class="mt-4 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      color="blue"
                      :label="t('KANBAN_VIEW.MANAGER.OPEN')"
                      @click="selectPipeline(pipeline.id)"
                    />
                    <Button
                      v-if="isAdmin"
                      size="sm"
                      color="slate"
                      variant="outline"
                      :label="t('KANBAN_VIEW.MANAGER.CONFIGURE')"
                      @click="openPipelineSettingsFor(pipeline)"
                    />
                    <Button
                      v-if="isAdmin"
                      size="sm"
                      color="slate"
                      variant="outline"
                      :label="t('KANBAN_VIEW.MANAGER.CLONE')"
                      :is-loading="isCloning === pipeline.id"
                      @click="clonePipeline(pipeline)"
                    />
                  </div>
                </article>
              </template>
            </Draggable>
            <div
              v-if="group.pipelines.length === 0 && !isFolderCollapsed(group.name)"
              class="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg border-2 border-dashed border-n-alpha-3 text-sm text-n-slate-10"
            >
              {{ t('KANBAN_VIEW.FOLDERS.DROP_HERE') }}
            </div>
          </div>
        </div>

        <!-- New folder button -->
        <div v-if="isAdmin" class="flex items-center gap-2">
          <template v-if="showNewFolderInput">
            <input
              v-model="newFolderName"
              class="rounded-lg border border-n-alpha-3 bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              :placeholder="t('KANBAN_VIEW.FOLDERS.NEW_FOLDER_PLACEHOLDER')"
              @keyup.enter="createFolder"
              @keyup.escape="showNewFolderInput = false; newFolderName = ''"
            />
            <Button size="sm" color="blue" :label="t('KANBAN_VIEW.FOLDERS.CREATE')" @click="createFolder" />
            <Button size="sm" color="slate" variant="ghost" :label="t('KANBAN_VIEW.FOLDERS.CANCEL')" @click="showNewFolderInput = false; newFolderName = ''" />
          </template>
          <button
            v-else
            type="button"
            class="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="showNewFolderInput = true"
          >
            <span class="i-lucide-folder-plus h-4 w-4" />
            {{ t('KANBAN_VIEW.FOLDERS.NEW_FOLDER') }}
          </button>
        </div>
      </div>
    </section>
    </div>

    <Draggable
      v-else-if="selectedPipelineId"
      v-model="boardStages"
      item-key="id"
      class="flex flex-1 min-h-0 gap-3 overflow-x-auto p-4 [&_.sortable-chosen]:scale-[0.98] [&_.sortable-chosen]:opacity-95 [&_.sortable-drag]:rotate-[1deg] [&_.sortable-drag]:shadow-[0_30px_50px_-24px_rgba(14,30,37,0.45)] [&_.sortable-ghost]:opacity-50 [&_.sortable-ghost]:ring-2 [&_.sortable-ghost]:ring-n-blue-7/45"
      :disabled="!isAdmin"
      handle=".stage-handle"
      ghost-class="sortable-ghost"
      chosen-class="sortable-chosen"
      drag-class="sortable-drag"
      @end="updateStageOrder"
    >
      <template #item="{ element: stage, index }">
        <section
          class="flex h-full min-w-[390px] max-w-[390px] flex-col rounded-2xl border border-n-alpha-3 border-t-4 bg-n-solid-1/95 shadow-sm"
          :class="stageAccentClass(stage, index)"
          :style="stage.color ? { borderTopColor: stage.color } : {}"
        >
          <header
            class="flex flex-col gap-1 rounded-t-2xl border-b border-n-alpha-3 bg-n-alpha-2 px-3 py-2"
          >
            <div class="flex min-w-0 items-center gap-2">
              <span
                class="stage-handle i-lucide-grip-vertical cursor-grab text-n-slate-10"
              />
              <h2 class="truncate text-sm font-semibold text-n-slate-12">
                {{ stage.name }}
              </h2>
              <span
                class="shrink-0 rounded px-1 py-0.5 font-mono text-[10px] text-n-slate-9"
                :title="`Stage ID: ${stage.id}`"
              >
                #{{ stage.id }}
              </span>
              <span
                class="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-n-alpha-2 px-1 text-xs text-n-slate-11"
              >
                {{ stage.items.length }}
              </span>
              <div
                v-if="automationsForStage(stage.id).length"
                class="relative ml-auto flex items-center"
                @click.stop
              >
                <button
                  class="flex cursor-pointer items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] transition-colors hover:opacity-80"
                  :class="automationsForStage(stage.id).some(r => r.active) ? 'bg-n-alpha-3 text-n-brand' : 'bg-n-alpha-2 text-n-slate-9'"
                  @click="activeStageConversionDropdown = null; activeStageAutomationDropdown = activeStageAutomationDropdown === stage.id ? null : stage.id"
                >
                  <span class="i-lucide-zap h-3 w-3" />
                  {{ automationsForStage(stage.id).length }}
                </button>
                <div
                  v-if="activeStageAutomationDropdown === stage.id"
                  class="absolute right-0 top-full z-30 mt-1 min-w-[220px] rounded-lg border border-n-alpha-3 bg-n-solid-1 p-2 shadow-lg"
                >
                  <p class="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-n-slate-10">
                    {{ t('KANBAN_VIEW.STAGE.AUTOMATIONS_TITLE') }}
                  </p>
                  <button
                    v-for="rule in automationsForStage(stage.id)"
                    :key="rule.id"
                    class="flex w-full cursor-pointer items-center gap-1.5 rounded px-1.5 py-1 hover:bg-n-alpha-1"
                    @click="openEditAutomationModal(rule)"
                  >
                    <span
                      class="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                      :class="rule.active ? 'bg-emerald-500' : 'bg-n-slate-7'"
                    />
                    <span class="truncate text-xs text-n-slate-11">{{ rule.name }}</span>
                    <span class="i-lucide-pencil ml-auto h-3 w-3 shrink-0 text-n-slate-9" />
                  </button>
                </div>
              </div>
              <div
                v-if="stage.triggers_conversion"
                class="relative flex items-center"
                :class="{ 'ml-auto': !automationsForStage(stage.id).length }"
                @click.stop
              >
                <button
                  class="flex cursor-pointer items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] transition-colors hover:opacity-80"
                  :class="stage.conversion_type?.has_monetary_value ? 'bg-emerald-500/15 text-emerald-600' : 'bg-n-alpha-3 text-n-slate-10'"
                  :title="stage.conversion_type?.has_monetary_value ? t('KANBAN_VIEW.STAGE.CONVERSION_MONETARY') : t('KANBAN_VIEW.STAGE.CONVERSION_NON_MONETARY')"
                  @click="activeStageAutomationDropdown = null; activeStageConversionDropdown = activeStageConversionDropdown === stage.id ? null : stage.id"
                >
                  <span
                    class="h-3 w-3"
                    :class="stage.conversion_type?.has_monetary_value ? 'i-lucide-circle-dollar-sign' : 'i-lucide-flag'"
                  />
                </button>
                <div
                  v-if="activeStageConversionDropdown === stage.id"
                  class="absolute right-0 top-full z-30 mt-1 min-w-[200px] rounded-lg border border-n-alpha-3 bg-n-solid-1 p-2 shadow-lg"
                >
                  <p class="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-n-slate-10">
                    {{ t('KANBAN_VIEW.STAGE.CONVERSION_TITLE') }}
                  </p>
                  <div v-if="stage.conversion_type" class="flex items-center gap-2 rounded px-1.5 py-1.5">
                    <span
                      class="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                      :style="{ backgroundColor: stage.conversion_type.color }"
                    />
                    <div class="flex flex-col gap-0.5">
                      <span class="text-xs font-medium text-n-slate-12">{{ stage.conversion_type.name }}</span>
                      <span class="text-[10px] text-n-slate-9">
                        {{ stage.conversion_type.has_monetary_value ? t('KANBAN_VIEW.STAGE.CONVERSION_MONETARY') : t('KANBAN_VIEW.STAGE.CONVERSION_NON_MONETARY') }}
                      </span>
                    </div>
                  </div>
                  <div v-else class="px-1.5 py-1.5 text-xs text-n-slate-9">
                    {{ t('KANBAN_VIEW.STAGE.CONVERSION_NO_TYPE') }}
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="stage.meta?.deal_value_total > 0"
              class="flex items-center gap-1 pl-6"
            >
              <span class="i-lucide-circle-dollar-sign text-xs text-n-teal-10" />
              <span class="text-xs font-semibold text-n-teal-11">
                {{ formatCurrency(stage.meta.deal_value_total) }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ t('KANBAN_VIEW.DEAL.STAGE_TOTAL') }}
              </span>
            </div>
            <div class="flex items-center justify-end gap-1">
              <Button
                size="xs"
                variant="outline"
                color="slate"
                icon="i-lucide-send"
                :label="t('KANBAN_VIEW.ACTIONS.CREATE_CAMPAIGN')"
                @click="openCampaignShortcutForStage(stage)"
              />
              <Button
                size="xs"
                variant="outline"
                color="slate"
                icon="i-lucide-sparkles"
                :label="t('KANBAN_VIEW.ACTIONS.AUTOMATION')"
                @click="openAutomationDialogForStage(stage)"
              />
            </div>
          </header>

          <div class="px-2 pt-2">
            <div class="rounded-lg bg-n-alpha-2 p-1">
              <Button
                size="xs"
                variant="ghost"
                color="slate"
                icon="i-lucide-user-plus"
                :label="t('KANBAN_VIEW.ACTIONS.QUICK_ADD_CONTACT')"
                @click="openAddCardDialog(stage.id)"
              />
            </div>
          </div>

          <Draggable
            v-model="stage.items"
            item-key="id"
            group="pipeline-cards"
            class="flex-1 space-y-2 overflow-y-auto p-2 [&_.sortable-chosen]:scale-[0.985] [&_.sortable-chosen]:cursor-grabbing [&_.sortable-drag]:rotate-[1.1deg] [&_.sortable-drag]:shadow-[0_28px_45px_-22px_rgba(0,0,0,0.35)] [&_.sortable-ghost]:opacity-45 [&_.sortable-ghost]:ring-2 [&_.sortable-ghost]:ring-n-blue-8/45"
            ghost-class="sortable-ghost"
            chosen-class="sortable-chosen"
            drag-class="sortable-drag"
            @change="event => handleCardChange(stage, event)"
          >
            <template #item="{ element: card }">
              <article
                class="group rounded-xl border border-n-alpha-3 bg-gradient-to-br from-n-solid-1 via-n-solid-1 to-n-alpha-1 p-3 transition-all duration-150 ease-out hover:-translate-y-0.5 hover:border-n-blue-7/35 hover:shadow-md"
              >
                <button
                  type="button"
                  class="block w-full text-left"
                  @click="openContactDetails(card)"
                >
                  <div class="mb-2 flex items-center gap-2">
                    <img
                      v-if="card.item.thumbnail"
                      :src="card.item.thumbnail"
                      :alt="card.item.name"
                      class="h-8 w-8 rounded-full object-cover ring-1 ring-n-alpha-3"
                    />
                    <span
                      v-else
                      class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-n-alpha-2 text-[10px] font-semibold text-n-slate-11"
                    >
                      {{ contactInitials(card) }}
                    </span>
                    <div class="min-w-0 flex-1">
                      <p class="truncate text-sm font-semibold text-n-slate-12">
                        {{ card.item.name || '-' }}
                      </p>
                      <p
                        v-if="card.item.company_name"
                        class="truncate text-xs font-medium text-n-brand"
                      >
                        {{ card.item.company_name }}
                      </p>
                      <p class="truncate text-xs text-n-slate-11">
                        {{ card.item.email || card.item.phone_number || '-' }}
                      </p>
                    </div>
                  </div>

                  <div class="flex items-center justify-between">
                    <p class="text-xs text-n-slate-11">
                      {{
                        t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.LAST_ACTIVITY')
                      }}
                    </p>
                    <p class="text-xs font-medium text-n-slate-12">
                      {{ formatTimestamp(card.item.last_activity_at) }}
                    </p>
                  </div>
                  <!-- Return date badge -->
                  <div v-if="card.return_date" class="mt-1.5 flex items-center gap-1.5">
                    <span class="i-lucide-calendar-clock text-xs" :class="card.return_date * 1000 < Date.now() ? 'text-ruby-11' : 'text-n-blue-11'" />
                    <span
                      class="rounded-full px-2 py-0.5 text-[10px] font-medium"
                      :class="card.return_date * 1000 < Date.now()
                        ? 'bg-ruby-3/30 text-ruby-11'
                        : 'bg-n-blue-3/20 text-n-blue-11'"
                    >
                      {{ formatTimestamp(card.return_date) }}
                    </span>
                  </div>
                </button>

                <div
                  class="mt-2 border-t border-n-alpha-2 pt-2"
                  @click.stop
                >
                  <div class="flex items-center justify-between">
                    <span class="text-xs text-n-slate-10">
                      {{ t('KANBAN_VIEW.DEAL.VALUE_LABEL') }}
                    </span>
                    <div
                      v-if="editingDealCard === card.id"
                      class="flex items-center gap-1"
                    >
                      <input
                        v-model="dealValueInput"
                        type="number"
                        step="0.01"
                        min="0"
                        class="w-28 rounded-md border border-n-alpha-4 bg-n-solid-1 px-2 py-0.5 text-right text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-blue-7"
                        @keyup.enter="saveDealValue(card)"
                        @keyup.escape="cancelEditDealValue"
                        @blur="saveDealValue(card)"
                      />
                    </div>
                    <button
                      v-else
                      type="button"
                      class="text-xs font-semibold text-n-teal-11 hover:text-n-teal-12"
                      @click.stop="startEditDealValue(card)"
                    >
                      {{
                        card.deal_value > 0
                          ? formatCurrency(card.deal_value)
                          : t('KANBAN_VIEW.DEAL.SET_VALUE')
                      }}
                    </button>
                  </div>
                </div>

                <div
                  v-if="hasProductsCatalog && products.length"
                  class="mt-2 border-t border-n-alpha-2 pt-2"
                  @click.stop
                >
                  <div class="flex items-center justify-between gap-1">
                    <span class="text-xs text-n-slate-10">{{ t('KANBAN_VIEW.DEAL.PRODUCT_LABEL') }}</span>
                    <button
                      class="text-xs text-n-brand hover:underline"
                      @click="activeProductDropdownCard = activeProductDropdownCard === card.id ? null : card.id"
                    >
                      {{ card.products?.length ? `${card.products.length} produto(s)` : t('KANBAN_VIEW.DEAL.NO_PRODUCT') }}
                    </button>
                  </div>
                  <div v-if="card.products?.length" class="mt-1 flex flex-wrap gap-1">
                    <span
                      v-for="p in card.products"
                      :key="p.id"
                      class="rounded-full px-2 py-0.5 text-xs font-medium text-white"
                      :style="{ backgroundColor: p.color || '#6366f1' }"
                    >{{ p.code }}</span>
                  </div>
                  <div v-if="activeProductDropdownCard === card.id" class="mt-1 rounded border border-n-alpha-3 bg-n-solid-1 p-1 shadow-sm">
                    <label
                      v-for="p in products"
                      :key="p.id"
                      class="flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-1 hover:bg-n-alpha-1"
                    >
                      <input
                        type="checkbox"
                        :checked="(card.product_ids || []).includes(p.id)"
                        class="h-3 w-3 accent-n-brand"
                        @change="toggleProduct(card, p.id)"
                      />
                      <span class="flex items-center gap-1 text-xs">
                        <span
                          v-if="p.color"
                          class="inline-block h-2 w-2 rounded-full"
                          :style="{ backgroundColor: p.color }"
                        />
                        {{ p.name }}
                        <span
                          v-if="p.category && CATEGORY_MAP[p.category]"
                          class="ml-0.5 rounded-full px-1.5 py-px text-[9px] font-semibold leading-none"
                          :class="CATEGORY_MAP[p.category].color"
                        >{{ CATEGORY_MAP[p.category].label }}</span>
                      </span>
                    </label>
                  </div>
                </div>

                <!-- Card Display Attributes (inline editable) -->
                <div
                  v-if="selectedPipeline?.card_display_attributes?.length && card.item?.custom_attributes !== undefined"
                  class="mt-2 border-t border-n-alpha-2 pt-2 space-y-1"
                  @click.stop
                >
                  <div
                    v-for="attrConfig in selectedPipeline.card_display_attributes.filter(a => a.attribute_key)"
                    :key="attrConfig.attribute_key"
                    class="flex items-center justify-between gap-2"
                  >
                    <span class="shrink-0 text-xs text-n-slate-10">{{ attrConfig.label || attrConfig.attribute_key }}</span>
                    <CardAttributeInlineEditor
                      :card="card"
                      :attr-config="attrConfig"
                      :attr-def="allCustomAttributes.find(a => a.attribute_key === attrConfig.attribute_key && a.attribute_model === attrConfig.attribute_model)"
                      @updated="val => {
                        if (!card.item) return;
                        if (attrConfig.attribute_model === 'contact_attribute' && card.item_type === 'Conversation') {
                          card.item.contact_custom_attributes = { ...(card.item.contact_custom_attributes || {}), [attrConfig.attribute_key]: val };
                        } else if (attrConfig.attribute_model === 'conversation_attribute' && card.item_type === 'Contact') {
                          card.item.conversation_custom_attributes = { ...(card.item.conversation_custom_attributes || {}), [attrConfig.attribute_key]: val };
                        } else {
                          card.item.custom_attributes = { ...(card.item.custom_attributes || {}), [attrConfig.attribute_key]: val };
                        }
                      }"
                    />
                  </div>
                </div>

                <div
                  v-if="stage.is_won && (card.won_count > 0 || card.total_won_value > 0)"
                  class="mt-2 border-t border-n-alpha-2 pt-2"
                  @click.stop
                >
                  <div class="flex items-center justify-between">
                    <span class="text-xs text-n-slate-10">Total ganho</span>
                    <span class="text-xs font-semibold text-emerald-600">{{ formatCurrency(card.total_won_value) }}</span>
                  </div>
                  <div class="flex items-center justify-between mt-0.5">
                    <span class="text-xs text-n-slate-10">Nº de vendas</span>
                    <span class="text-xs font-semibold text-n-slate-11">{{ card.won_count }}x</span>
                  </div>
                </div>

                <div
                  class="mt-2 flex items-center justify-end gap-1"
                  @click.stop
                >
                  <Button
                    v-if="stage.is_won"
                    size="xs"
                    color="slate"
                    variant="faded"
                    :label="t('KANBAN_VIEW.ACTIONS.NEW_JOURNEY')"
                    @click="restartJourney(card)"
                  />
                  <Button
                    size="xs"
                    color="ruby"
                    variant="faded"
                    :label="t('KANBAN_VIEW.ACTIONS.REMOVE')"
                    @click="removeItem(card)"
                  />
                </div>
              </article>
            </template>
          </Draggable>

          <Button
            v-if="stage.meta?.has_more"
            size="sm"
            variant="outline"
            color="slate"
            class="m-2"
            :label="t('KANBAN_VIEW.ACTIONS.LOAD_MORE')"
            @click="loadMore(stage)"
          />
        </section>
      </template>
    </Draggable>

    <div
      v-if="isPipelineSettingsOpen"
      class="fixed inset-0 z-[1200] bg-n-alpha-black1/70 backdrop-blur-sm"
    >
      <div class="h-full w-full overflow-y-auto p-4 md:p-6">
        <div
          class="mx-auto flex min-h-full w-full max-w-[1600px] flex-col rounded-2xl border border-n-alpha-3 bg-n-solid-1 shadow-xl"
        >
          <header
            class="sticky top-0 z-10 flex items-start justify-between border-b border-n-alpha-3 bg-n-solid-1/95 px-4 py-3 backdrop-blur-sm"
          >
            <div>
              <h2 class="text-lg font-semibold text-n-slate-12">
                {{ t('KANBAN_VIEW.DIALOGS.PIPELINE_SETTINGS.TITLE') }}
              </h2>
              <p class="text-sm text-n-slate-11">
                {{ t('KANBAN_VIEW.DIALOGS.PIPELINE_SETTINGS.DESCRIPTION') }}
              </p>
            </div>
            <button
              type="button"
              class="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-n-alpha-3 text-n-slate-11 transition hover:border-n-alpha-5 hover:text-n-slate-12"
              :aria-label="t('KANBAN_VIEW.ACTIONS.CLOSE')"
              @click="closePipelineSettingsDialog"
            >
              <span class="i-lucide-x text-lg" />
            </button>
          </header>

          <div
            class="grid flex-1 grid-cols-1 gap-4 overflow-hidden p-4 md:grid-cols-12"
          >
            <aside class="space-y-3 md:col-span-4">
              <div class="rounded-xl border border-n-alpha-3 bg-n-alpha-1 p-3">
                <p class="text-xs text-n-slate-10">
                  {{ t('KANBAN_VIEW.STATS.STAGES') }}
                </p>
                <p class="text-lg font-semibold text-n-slate-12">
                  {{ stageSettings.length }}
                </p>
              </div>
              <div class="rounded-xl border border-n-alpha-3 bg-n-alpha-1 p-3">
                <p class="text-xs text-n-slate-10">
                  {{ t('KANBAN_VIEW.STATS.CARDS') }}
                </p>
                <p class="text-lg font-semibold text-n-slate-12">
                  {{
                    stageSettings.reduce(
                      (total, stage) => total + (stage.card_count || 0),
                      0
                    )
                  }}
                </p>
              </div>

              <Input
                v-model="pipelineSettingsForm.name"
                :label="t('KANBAN_VIEW.FIELDS.NAME')"
              />
              <Input
                v-model="pipelineSettingsForm.description"
                :label="t('KANBAN_VIEW.FIELDS.DESCRIPTION')"
              />

              <div class="border-t border-n-alpha-3 pt-3 mt-1">
                <p class="text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('KANBAN_VIEW.FIELDS.VISIBILITY_AGENTS') }}
                </p>
                <p class="text-xs text-n-slate-9 mb-2">
                  {{ t('KANBAN_VIEW.FIELDS.VISIBILITY_AGENTS_HINT') }}
                </p>
                <multiselect
                  v-model="pipelineSettingsForm.restrictedAgents"
                  :options="agents"
                  :multiple="true"
                  :close-on-select="false"
                  label="name"
                  track-by="id"
                  :loading="isLoadingAgents"
                  :placeholder="t('KANBAN_VIEW.FIELDS.VISIBILITY_AGENTS_PLACEHOLDER')"
                  selected-label=""
                  select-label=""
                  deselect-label=""
                  class="text-sm"
                />
              </div>

              <!-- CARD DISPLAY ATTRIBUTES -->
              <div class="border-t border-n-alpha-3 pt-3 mt-1">
                <p class="text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('KANBAN_VIEW.STAGE_CONFIG.CARD_DISPLAY_TITLE') }}
                </p>
                <p class="text-xs text-n-slate-9 mb-2">
                  {{ t('KANBAN_VIEW.STAGE_CONFIG.CARD_DISPLAY_HINT') }}
                </p>

                <div class="space-y-1.5">
                  <div
                    v-for="(attr, idx) in pipelineSettingsForm.card_display_attributes"
                    :key="idx"
                    class="flex items-center gap-1"
                  >
                    <select
                      v-model="attr.attribute_model"
                      class="w-24 rounded border border-n-weak bg-n-background px-2 py-1 text-xs text-n-slate-12"
                    >
                      <option value="contact_attribute">{{ t('KANBAN_VIEW.STAGE_CONFIG.ATTR_MODEL_CONTACT') }}</option>
                      <option value="conversation_attribute">{{ t('KANBAN_VIEW.STAGE_CONFIG.ATTR_MODEL_CONVERSATION') }}</option>
                    </select>
                    <select
                      v-model="attr.attribute_key"
                      class="flex-1 rounded border border-n-weak bg-n-background px-2 py-1 text-xs text-n-slate-12"
                      @change="onCardDisplayAttrChange(attr, attr.attribute_key)"
                    >
                      <option value="">{{ t('KANBAN_VIEW.STAGE_CONFIG.SELECT_ATTRIBUTE') }}</option>
                      <option
                        v-for="a in allCustomAttributes.filter(a => a.attribute_model === attr.attribute_model)"
                        :key="a.attribute_key"
                        :value="a.attribute_key"
                      >
                        {{ a.attribute_display_name }}
                      </option>
                    </select>
                    <button
                      type="button"
                      class="shrink-0 rounded p-1 text-n-red-11 hover:bg-n-red-2"
                      @click="removeCardDisplayAttr(idx)"
                    >
                      <span class="i-lucide-trash-2 size-3.5" />
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  class="mt-1.5 flex items-center gap-1 text-xs text-n-brand hover:underline"
                  @click="addCardDisplayAttr"
                >
                  <span class="i-lucide-plus size-3.5" />
                  {{ t('KANBAN_VIEW.STAGE_CONFIG.CARD_DISPLAY_ADD') }}
                </button>
              </div>

              <!-- WHATSAPP ALERT CONFIG -->
              <div class="border-t border-n-alpha-3 pt-3 mt-1">
                <p class="text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('KANBAN_VIEW.ALERT_CONFIG.TITLE') }}
                </p>
                <p class="text-xs text-n-slate-9 mb-2">
                  {{ t('KANBAN_VIEW.ALERT_CONFIG.DESCRIPTION') }}
                </p>

                <label class="flex items-center gap-2 mb-2 cursor-pointer">
                  <input
                    v-model="pipelineSettingsForm.settings.alert_enabled"
                    type="checkbox"
                    class="size-4 rounded border-n-weak accent-n-brand"
                  />
                  <span class="text-xs text-n-slate-12">
                    {{ t('KANBAN_VIEW.ALERT_CONFIG.ENABLE') }}
                  </span>
                </label>

                <template v-if="pipelineSettingsForm.settings.alert_enabled">
                  <div class="mb-2">
                    <label class="mb-1 block text-xs text-n-slate-11">
                      {{ t('KANBAN_VIEW.ALERT_CONFIG.WHATSAPP_INBOX') }}
                    </label>
                    <select
                      v-model="pipelineSettingsForm.settings.alert_whatsapp_inbox_id"
                      class="w-full rounded border border-n-weak bg-n-background px-2 py-1.5 text-xs text-n-slate-12"
                    >
                      <option value="">
                        {{ t('KANBAN_VIEW.ALERT_CONFIG.SELECT_INBOX') }}
                      </option>
                      <option
                        v-for="inbox in inboxes.filter(i => i.channel_type === 'Channel::Whatsapp')"
                        :key="inbox.id"
                        :value="inbox.id"
                      >
                        {{ inbox.name }}
                      </option>
                    </select>
                  </div>

                  <div class="mb-2">
                    <label class="mb-1 block text-xs text-n-slate-11">
                      {{ t('KANBAN_VIEW.ALERT_CONFIG.PHONE_NUMBERS') }}
                    </label>
                    <input
                      v-model="pipelineSettingsForm.settings.alert_whatsapp_numbers_raw"
                      type="text"
                      class="w-full rounded border border-n-weak bg-n-background px-2 py-1.5 text-xs text-n-slate-12"
                      :placeholder="t('KANBAN_VIEW.ALERT_CONFIG.PHONE_NUMBERS_PLACEHOLDER')"
                    />
                    <p class="mt-0.5 text-[10px] text-n-slate-9">
                      {{ t('KANBAN_VIEW.ALERT_CONFIG.PHONE_NUMBERS_HINT') }}
                    </p>
                  </div>
                </template>
              </div>

              <div class="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  color="blue"
                  :label="t('KANBAN_VIEW.DIALOGS.PIPELINE_SETTINGS.CONFIRM')"
                  :is-loading="isSaving"
                  :disabled="!pipelineSettingsForm.name"
                  @click="updatePipelineSettings"
                />
                <Button
                  size="sm"
                  color="ruby"
                  variant="outline"
                  :label="t('KANBAN_VIEW.ACTIONS.DELETE_PIPELINE')"
                  :is-loading="isSaving"
                  @click="requestDeletePipeline"
                />
              </div>
            </aside>

            <section
              class="rounded-xl border border-n-alpha-3 bg-n-background p-3 md:col-span-8"
            >
              <div
                class="mb-3 flex flex-wrap items-center justify-between gap-2"
              >
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ t('KANBAN_VIEW.DIALOGS.PIPELINE_SETTINGS.STAGES_TITLE') }}
                </h3>
                <div class="flex flex-wrap gap-2">
                  <Button
                    size="xs"
                    color="blue"
                    variant="outline"
                    :label="t('KANBAN_VIEW.ACTIONS.CREATE_STAGE')"
                    @click="openCreateStageDialog"
                  />
                  <Button
                    size="xs"
                    color="slate"
                    variant="outline"
                    :label="t('KANBAN_VIEW.ACTIONS.SAVE_STAGE_ORDER')"
                    :disabled="!stageOrderChanged"
                    :is-loading="isSaving"
                    @click="saveStageOrder"
                  />
                </div>
              </div>

              <div v-if="!stageSettings.length" class="text-sm text-n-slate-11">
                {{ t('KANBAN_VIEW.DIALOGS.PIPELINE_SETTINGS.NO_STAGES') }}
              </div>

              <Draggable
                v-else
                v-model="stageSettings"
                item-key="id"
                handle=".stage-sort-handle"
                class="max-h-[62vh] space-y-2 overflow-y-auto pr-1"
                ghost-class="sortable-ghost"
                chosen-class="sortable-chosen"
                drag-class="sortable-drag"
                @end="onStagesReordered"
              >
                <template #item="{ element: stage }">
                  <div class="rounded-lg border border-n-alpha-3 p-2">
                    <div class="mb-2 flex items-center justify-between gap-2">
                      <div class="flex items-center gap-2">
                        <span
                          class="stage-sort-handle i-lucide-grip-vertical cursor-grab text-n-slate-10"
                        />
                        <span class="text-xs text-n-slate-10">
                          {{
                            t(
                              'KANBAN_VIEW.DIALOGS.PIPELINE_SETTINGS.CARD_COUNT',
                              {
                                count: stage.card_count || 0,
                              }
                            )
                          }}
                        </span>
                      </div>
                    </div>

                    <div class="grid grid-cols-1 gap-2 md:grid-cols-3">
                      <Input
                        v-model="stage.name"
                        :label="t('KANBAN_VIEW.FIELDS.NAME')"
                        custom-input-class="!mb-0"
                      />
                      <div class="flex flex-col gap-1">
                        <label class="text-xs font-medium text-n-slate-11">
                          {{ t('KANBAN_VIEW.FIELDS.COLOR') }}
                        </label>
                        <div class="flex items-center gap-2">
                          <input
                            v-model="stage.color"
                            type="color"
                            class="h-8 w-10 rounded border border-n-alpha-3 bg-transparent p-0"
                          />
                          <Input
                            v-model="stage.color"
                            :placeholder="
                              t('KANBAN_VIEW.FIELDS.COLOR_PLACEHOLDER')
                            "
                            custom-input-class="!mb-0"
                          />
                        </div>
                      </div>
                      <div class="flex items-end gap-1">
                        <Button
                          size="sm"
                          color="blue"
                          variant="outline"
                          :label="t('KANBAN_VIEW.ACTIONS.SAVE_STAGE')"
                          :is-loading="stage.isSaving"
                          @click="updateStageSettings(stage)"
                        />
                        <Button
                          size="sm"
                          color="slate"
                          variant="faded"
                          icon="i-lucide-copy"
                          :label="t('KANBAN_VIEW.ACTIONS.DUPLICATE_STAGE')"
                          :is-loading="stage.isSaving"
                          @click="duplicateStage(stage)"
                        />
                      </div>
                      <div class="col-span-full flex items-center gap-3 pt-1">
                        <label class="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            v-model="stage.triggers_conversion"
                            type="checkbox"
                            class="size-4 rounded accent-n-brand"
                          />
                          <span class="text-xs font-medium text-n-slate-12">
                            {{ t('KANBAN_VIEW.FIELDS.TRIGGERS_CONVERSION') }}
                          </span>
                        </label>
                        <select
                          v-if="stage.triggers_conversion"
                          v-model="stage.conversion_type_id"
                          class="text-xs border border-n-weak rounded-lg px-2 py-1 bg-n-background text-n-slate-12 h-7"
                        >
                          <option value="">{{ t('KANBAN_VIEW.FIELDS.CONVERSION_TYPE_PLACEHOLDER') }}</option>
                          <option v-for="ct in conversionTypes" :key="ct.id" :value="ct.id">{{ ct.name }}</option>
                        </select>
                        <span v-if="stage.triggers_conversion" class="ml-1" />
                        <label v-if="stage.triggers_conversion" class="flex items-center gap-1.5 cursor-pointer select-none">
                          <input
                            v-model="stage.conversion_records_value"
                            type="checkbox"
                            class="size-3.5 rounded accent-n-brand"
                          />
                          <span class="text-xs text-n-slate-11 flex items-center gap-1">
                            <span class="i-lucide-circle-dollar-sign size-3.5" />
                            {{ t('KANBAN_VIEW.FIELDS.CONVERSION_RECORDS_VALUE') }}
                          </span>
                        </label>
                        <span v-if="stage.is_won" class="ml-auto text-xs font-semibold text-emerald-600 flex items-center gap-1">
                          <span class="i-lucide-trophy size-3.5" />
                          {{ t('KANBAN_VIEW.FIELDS.WON_STAGE') }}
                        </span>
                      </div>

                      <!-- ATTRIBUTE AUTOMATIONS -->
                      <div class="col-span-full mt-1 rounded-lg border border-n-alpha-3 bg-n-alpha-1 p-2">
                        <button
                          type="button"
                          class="flex w-full items-center justify-between text-left"
                          @click="expandedStageConfigPanel = expandedStageConfigPanel === `auto_${stage.id}` ? null : `auto_${stage.id}`"
                        >
                          <span class="flex items-center gap-1.5 text-xs font-semibold text-n-slate-12">
                            <span class="i-lucide-zap size-3.5 text-n-brand" />
                            {{ t('KANBAN_VIEW.STAGE_CONFIG.AUTOMATIONS_TITLE') }}
                            <span
                              v-if="(stage.stage_automations || []).length"
                              class="rounded-full bg-n-brand px-1.5 py-0.5 text-[10px] text-white"
                            >
                              {{ stage.stage_automations.length }}
                            </span>
                          </span>
                          <span
                            class="i-lucide-chevron-right size-3.5 text-n-slate-10 transition-transform duration-150"
                            :class="{ 'rotate-90': expandedStageConfigPanel === `auto_${stage.id}` }"
                          />
                        </button>

                        <div v-if="expandedStageConfigPanel === `auto_${stage.id}`" class="mt-2 space-y-2">
                          <p class="text-xs text-n-slate-10">{{ t('KANBAN_VIEW.STAGE_CONFIG.AUTOMATIONS_HINT') }}</p>

                          <!-- move_to_pipeline automations -->
                          <div
                            v-for="(rule, rIdx) in (stage.stage_automations || []).filter(r => r.action === 'move_to_pipeline')"
                            :key="'mtp_' + rIdx"
                            class="flex items-center gap-2 rounded-lg border border-n-teal-6 bg-n-teal-2 p-2"
                          >
                            <span class="i-lucide-arrow-right-circle size-4 shrink-0 text-n-teal-11" />
                            <div class="flex-1">
                              <p class="text-xs font-medium text-n-teal-12">
                                {{ rule.label || 'Mover para outro funil' }}
                              </p>
                              <p class="text-[10px] text-n-teal-10">
                                Ao entrar nesta etapa, o lead sera movido para o funil
                                {{ pipelineNameById(rule.target_pipeline_id) }}
                              </p>
                            </div>
                            <select
                              v-model="rule.target_pipeline_id"
                              class="rounded border border-n-teal-6 bg-white px-2 py-1 text-xs text-n-slate-12"
                              @change="onMoveToPipelineChanged(rule)"
                            >
                              <option value="">Selecione o funil...</option>
                              <option
                                v-for="p in pipelines.filter(pp => pp.id !== selectedPipelineId)"
                                :key="p.id"
                                :value="p.id"
                              >
                                {{ p.folder ? `[${p.folder}] ` : '' }}{{ p.name }}
                              </option>
                            </select>
                            <button
                              type="button"
                              class="shrink-0 rounded p-1 text-n-red-11 hover:bg-n-red-2"
                              @click="removeAutomationRule(stage, rule.id)"
                            >
                              <span class="i-lucide-trash-2 size-3.5" />
                            </button>
                          </div>

                          <!-- attribute-based automations -->
                          <div
                            v-for="rule in (stage.stage_automations || []).filter(r => r.action !== 'move_to_pipeline')"
                            :key="rule.id"
                            class="grid grid-cols-2 gap-1.5 rounded-lg border border-n-alpha-3 bg-n-solid-1 p-2 md:grid-cols-4"
                          >
                            <select
                              v-model="rule.attribute_model"
                              class="rounded border border-n-weak bg-n-background px-2 py-1 text-xs text-n-slate-12"
                            >
                              <option value="contact_attribute">{{ t('KANBAN_VIEW.STAGE_CONFIG.ATTR_MODEL_CONTACT') }}</option>
                              <option value="conversation_attribute">{{ t('KANBAN_VIEW.STAGE_CONFIG.ATTR_MODEL_CONVERSATION') }}</option>
                            </select>

                            <select
                              v-model="rule.attribute_key"
                              class="rounded border border-n-weak bg-n-background px-2 py-1 text-xs text-n-slate-12"
                            >
                              <option value="">{{ t('KANBAN_VIEW.STAGE_CONFIG.SELECT_ATTRIBUTE') }}</option>
                              <option
                                v-for="attr in allCustomAttributes.filter(a => a.attribute_model === rule.attribute_model)"
                                :key="attr.attribute_key"
                                :value="attr.attribute_key"
                              >
                                {{ attr.attribute_display_name }}
                              </option>
                            </select>

                            <select
                              v-model="rule.condition"
                              class="rounded border border-n-weak bg-n-background px-2 py-1 text-xs text-n-slate-12"
                            >
                              <option value="filled">{{ t('KANBAN_VIEW.STAGE_CONFIG.CONDITION_FILLED') }}</option>
                            </select>

                            <div class="flex items-center gap-1">
                              <select
                                v-model="rule.target_stage_id"
                                class="flex-1 rounded border border-n-weak bg-n-background px-2 py-1 text-xs text-n-slate-12"
                              >
                                <option value="">{{ t('KANBAN_VIEW.STAGE_CONFIG.SELECT_TARGET_STAGE') }}</option>
                                <option
                                  v-for="s in stageSettings.filter(s => s.id !== stage.id)"
                                  :key="s.id"
                                  :value="s.id"
                                >
                                  {{ s.name }}
                                </option>
                              </select>
                              <button
                                type="button"
                                class="shrink-0 rounded p-1 text-n-red-11 hover:bg-n-red-2"
                                @click="removeAutomationRule(stage, rule.id)"
                              >
                                <span class="i-lucide-trash-2 size-3.5" />
                              </button>
                            </div>
                          </div>

                          <div class="flex items-center gap-2">
                            <button
                              type="button"
                              class="flex items-center gap-1 text-xs text-n-brand hover:underline"
                              @click="addAutomationRule(stage)"
                            >
                              <span class="i-lucide-plus size-3.5" />
                              {{ t('KANBAN_VIEW.STAGE_CONFIG.ADD_AUTOMATION') }}
                            </button>
                            <button
                              type="button"
                              class="flex items-center gap-1 text-xs text-n-teal-11 hover:underline"
                              @click="addMoveToPipelineRule(stage)"
                            >
                              <span class="i-lucide-arrow-right-circle size-3.5" />
                              Mover para outro funil
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- ENTRY REQUIREMENTS (GATE) -->
                      <div class="col-span-full mt-1 rounded-lg border border-n-alpha-3 bg-n-alpha-1 p-2">
                        <button
                          type="button"
                          class="flex w-full items-center justify-between text-left"
                          @click="expandedStageConfigPanel = expandedStageConfigPanel === `gate_${stage.id}` ? null : `gate_${stage.id}`"
                        >
                          <span class="flex items-center gap-1.5 text-xs font-semibold text-n-slate-12">
                            <span class="i-lucide-lock size-3.5 text-amber-500" />
                            {{ t('KANBAN_VIEW.STAGE_CONFIG.GATE_TITLE') }}
                            <span
                              v-if="stage.entry_requirements?.enabled"
                              class="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] text-white"
                            >
                              {{ t('KANBAN_VIEW.STAGE_CONFIG.GATE_ACTIVE') }}
                            </span>
                          </span>
                          <span
                            class="i-lucide-chevron-right size-3.5 text-n-slate-10 transition-transform duration-150"
                            :class="{ 'rotate-90': expandedStageConfigPanel === `gate_${stage.id}` }"
                          />
                        </button>

                        <div v-if="expandedStageConfigPanel === `gate_${stage.id}`" class="mt-2 space-y-2">
                          <label class="flex cursor-pointer select-none items-center gap-2">
                            <input
                              v-model="stage.entry_requirements.enabled"
                              type="checkbox"
                              class="size-4 rounded accent-amber-500"
                            />
                            <span class="text-xs font-medium text-n-slate-12">
                              {{ t('KANBAN_VIEW.STAGE_CONFIG.GATE_ENABLE') }}
                            </span>
                          </label>

                          <div v-if="stage.entry_requirements?.enabled" class="space-y-1.5">
                            <p class="text-xs text-n-slate-10">{{ t('KANBAN_VIEW.STAGE_CONFIG.GATE_HINT') }}</p>

                            <div
                              v-for="(attr, idx) in (stage.entry_requirements.attributes || [])"
                              :key="idx"
                              class="flex items-center gap-1.5"
                            >
                              <select
                                v-model="attr.model"
                                class="w-24 shrink-0 rounded border border-n-weak bg-n-background px-2 py-1 text-xs text-n-slate-12"
                              >
                                <option value="contact_attribute">{{ t('KANBAN_VIEW.STAGE_CONFIG.ATTR_MODEL_CONTACT') }}</option>
                                <option value="conversation_attribute">{{ t('KANBAN_VIEW.STAGE_CONFIG.ATTR_MODEL_CONVERSATION') }}</option>
                              </select>
                              <select
                                v-model="attr.key"
                                class="min-w-0 flex-1 rounded border border-n-weak bg-n-background px-2 py-1 text-xs text-n-slate-12"
                              >
                                <option value="">{{ t('KANBAN_VIEW.STAGE_CONFIG.SELECT_ATTRIBUTE') }}</option>
                                <option
                                  v-for="a in allCustomAttributes.filter(a => a.attribute_model === attr.model)"
                                  :key="a.attribute_key"
                                  :value="a.attribute_key"
                                >
                                  {{ a.attribute_display_name }}
                                </option>
                              </select>
                              <button
                                type="button"
                                class="shrink-0 rounded p-1 text-n-red-11 hover:bg-n-red-2"
                                @click="removeEntryRequirementAttr(stage, idx)"
                              >
                                <span class="i-lucide-trash-2 size-3.5" />
                              </button>
                            </div>

                            <button
                              type="button"
                              class="flex items-center gap-1 text-xs text-amber-600 hover:underline"
                              @click="addEntryRequirementAttr(stage)"
                            >
                              <span class="i-lucide-plus size-3.5" />
                              {{ t('KANBAN_VIEW.STAGE_CONFIG.GATE_ADD_ATTR') }}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div class="flex items-end justify-end">
                        <Button
                          size="sm"
                          color="ruby"
                          variant="faded"
                          :label="t('KANBAN_VIEW.ACTIONS.DELETE_STAGE')"
                          :is-loading="stage.isSaving"
                          :disabled="stage.is_won"
                          @click="requestDeleteStage(stage)"
                        />
                      </div>
                    </div>
                  </div>
                </template>
              </Draggable>
            </section>
          </div>
        </div>
      </div>
    </div>

    <Dialog
      ref="deletePipelineDialogRef"
      type="alert"
      :title="t('KANBAN_VIEW.DIALOGS.PIPELINE_SETTINGS.DELETE_TITLE')"
      :description="t('KANBAN_VIEW.DIALOGS.PIPELINE_SETTINGS.DELETE_CONFIRM')"
      :confirm-button-label="t('KANBAN_VIEW.ACTIONS.DELETE_PIPELINE')"
      :is-loading="isSaving"
      @confirm="deletePipeline"
    />

    <Dialog
      ref="deleteStageDialogRef"
      type="alert"
      :title="t('KANBAN_VIEW.DIALOGS.PIPELINE_SETTINGS.DELETE_STAGE_TITLE')"
      :description="
        t('KANBAN_VIEW.DIALOGS.PIPELINE_SETTINGS.DELETE_STAGE_CONFIRM')
      "
      :confirm-button-label="t('KANBAN_VIEW.ACTIONS.DELETE_STAGE')"
      :is-loading="stagePendingDelete?.isSaving"
      @confirm="deleteStage"
    />

    <!-- Gate Entry Requirement Error Dialog -->
    <Dialog
      ref="gateErrorDialog"
      type="alert"
      :title="t('KANBAN_VIEW.GATE.BLOCKED_TITLE')"
      :show-confirm-button="false"
      :cancel-button-label="t('KANBAN_VIEW.ACTIONS.CLOSE')"
    >
      <div class="space-y-2">
        <p class="text-sm text-n-slate-11">
          {{ t('KANBAN_VIEW.GATE.BLOCKED_DESCRIPTION') }}
        </p>
        <div class="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
          <p class="mb-1.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
            {{ t('KANBAN_VIEW.GATE.MISSING_FIELDS') }}
          </p>
          <ul class="space-y-1">
            <li
              v-for="attr in gateErrorMissingAttrs"
              :key="attr.key"
              class="flex items-center gap-2 text-xs text-amber-800 dark:text-amber-300"
            >
              <span class="i-lucide-circle-alert size-3.5 shrink-0" />
              {{ attr.label || attr.key }}
              <span class="rounded bg-amber-100 px-1 py-0.5 text-[10px] text-amber-600 dark:bg-amber-800 dark:text-amber-200">
                {{ attr.model === 'contact_attribute' ? t('KANBAN_VIEW.STAGE_CONFIG.ATTR_MODEL_CONTACT') : t('KANBAN_VIEW.STAGE_CONFIG.ATTR_MODEL_CONVERSATION') }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="createPipelineDialogRef"
      :title="t('KANBAN_VIEW.DIALOGS.CREATE_PIPELINE.TITLE')"
      :description="t('KANBAN_VIEW.DIALOGS.CREATE_PIPELINE.DESCRIPTION')"
      :confirm-button-label="t('KANBAN_VIEW.DIALOGS.CREATE_PIPELINE.CONFIRM')"
      :disable-confirm-button="!pipelineForm.name || isSaving"
      :is-loading="isSaving"
      @confirm="createPipeline"
    >
      <div class="space-y-3">
        <Input
          v-model="pipelineForm.name"
          :label="t('KANBAN_VIEW.FIELDS.NAME')"
        />
        <Input
          v-model="pipelineForm.description"
          :label="t('KANBAN_VIEW.FIELDS.DESCRIPTION')"
        />
      </div>
    </Dialog>

    <Dialog
      ref="createStageDialogRef"
      :title="t('KANBAN_VIEW.DIALOGS.CREATE_STAGE.TITLE')"
      :description="t('KANBAN_VIEW.DIALOGS.CREATE_STAGE.DESCRIPTION')"
      :confirm-button-label="t('KANBAN_VIEW.DIALOGS.CREATE_STAGE.CONFIRM')"
      :disable-confirm-button="!stageForm.name || isSaving"
      :is-loading="isSaving"
      @confirm="createStage"
    >
      <div class="space-y-3">
        <Input v-model="stageForm.name" :label="t('KANBAN_VIEW.FIELDS.NAME')" />
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('KANBAN_VIEW.FIELDS.COLOR') }}
          </label>
          <div class="flex items-center gap-2">
            <input
              v-model="stageForm.color"
              type="color"
              class="h-9 w-12 rounded border border-n-alpha-3 bg-transparent p-0"
            />
            <Input
              v-model="stageForm.color"
              :placeholder="t('KANBAN_VIEW.FIELDS.COLOR_RANDOM_HINT')"
              custom-input-class="!mb-0"
            />
          </div>
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="addCardDialogRef"
      width="2xl"
      :title="t('KANBAN_VIEW.DIALOGS.ADD_CARD.TITLE')"
      :description="t('KANBAN_VIEW.DIALOGS.ADD_CARD.DESCRIPTION')"
      :show-confirm-button="false"
      @close="closeAddCardDialog"
    >
      <div class="space-y-3">
        <Input
          v-model="addCardSearch"
          :placeholder="t('KANBAN_VIEW.DIALOGS.ADD_CARD.SEARCH_PLACEHOLDER')"
        />

        <div
          class="max-h-80 overflow-y-auto rounded-xl border border-n-alpha-3 bg-n-background"
        >
          <div
            v-if="!addCardCandidates.length"
            class="px-4 py-8 text-center text-sm text-n-slate-11"
          >
            {{ t('KANBAN_VIEW.EMPTY.CANDIDATES') }}
          </div>

          <div
            v-for="candidate in addCardCandidates"
            :key="candidate.id"
            class="flex items-center justify-between gap-3 border-b border-n-alpha-3 px-3 py-2 last:border-b-0"
          >
            <div class="flex min-w-0 items-center gap-2">
              <img
                v-if="candidate.thumbnail"
                :src="candidate.thumbnail"
                :alt="candidate.name"
                class="h-7 w-7 rounded-full object-cover"
              />
              <span
                v-else
                class="inline-flex h-7 w-7 items-center justify-center rounded-full bg-n-alpha-2 text-[10px] font-semibold text-n-slate-11"
              >
                {{ (candidate.name || 'NA').slice(0, 2).toUpperCase() }}
              </span>
              <div class="min-w-0">
                <p class="truncate text-sm font-medium text-n-slate-12">
                  {{ candidate.name }}
                </p>
                <p class="truncate text-xs text-n-slate-11">
                  {{ candidate.email || candidate.phone_number || '-' }}
                </p>
              </div>
            </div>
            <Button
              size="xs"
              color="teal"
              :is-loading="isAddingCard"
              :label="t('KANBAN_VIEW.DIALOGS.ADD_CARD.ADD')"
              @click="addCardToPipeline(candidate)"
            />
          </div>
        </div>
      </div>
    </Dialog>

    <!-- ═══ CONTACT DETAILS MODAL (redesigned) ═══ -->
    <div
      v-if="isContactDetailsOpen"
      class="fixed inset-0 z-[1200] flex items-start justify-center bg-n-alpha-black1/60 backdrop-blur-[3px]"
      @click.self="closeContactDetails"
    >
      <div class="my-4 flex h-[calc(100vh-2rem)] w-full max-w-[1200px] flex-col overflow-hidden rounded-2xl border border-n-alpha-3 bg-n-solid-1 shadow-2xl md:my-6 md:h-[calc(100vh-3rem)]">
        <!-- Modal Header -->
        <header class="flex shrink-0 items-center justify-between border-b border-n-alpha-3 px-5 py-3">
          <div class="flex items-center gap-3">
            <img
              v-if="selectedContact?.thumbnail"
              :src="selectedContact.thumbnail"
              :alt="selectedContact.name"
              class="h-10 w-10 rounded-full object-cover ring-2 ring-n-alpha-3"
            />
            <span
              v-else
              class="inline-flex h-10 w-10 items-center justify-center rounded-full bg-n-brand/10 text-sm font-bold text-n-brand"
            >
              {{ contactInitials(selectedContactCard || {}) }}
            </span>
            <div class="min-w-0">
              <h2 class="truncate text-base font-semibold text-n-slate-12">
                {{ selectedContact?.name || '-' }}
              </h2>
              <p class="truncate text-xs text-n-slate-11">
                {{ selectedContact?.email || selectedContact?.phone_number || '-' }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Button
              size="xs"
              color="slate"
              variant="outline"
              icon="i-lucide-external-link"
              :label="t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.VIEW_PROFILE')"
              @click="router.push(accountScopedRoute('contacts_edit', { contactId: selectedContact?.id }))"
            />
            <button
              type="button"
              class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-n-slate-11 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
              @click="closeContactDetails"
            >
              <span class="i-lucide-x text-lg" />
            </button>
          </div>
        </header>

        <!-- Modal Body: 2-column layout -->
        <div class="flex min-h-0 flex-1">
          <!-- LEFT SIDEBAR: Contact info + Actions -->
          <aside class="flex w-80 shrink-0 flex-col overflow-y-auto border-r border-n-alpha-3 bg-n-alpha-1/50">
            <div class="space-y-4 p-4">
              <!-- Contact edit / info -->
              <div v-if="isEditingContact" class="space-y-2">
                <Input v-model="contactEditForm.name" :label="t('KANBAN_VIEW.FIELDS.NAME')" custom-input-class="!mb-0" />
                <Input v-model="contactEditForm.email" :label="t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.EMAIL')" custom-input-class="!mb-0" />
                <Input v-model="contactEditForm.phone_number" :label="t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.PHONE')" custom-input-class="!mb-0" />
                <div class="flex gap-2">
                  <Button size="xs" color="blue" :label="t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.SAVE')" :is-loading="isSavingContact" @click="saveContactDetails" />
                  <Button size="xs" color="slate" variant="outline" :label="t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.CANCEL')" @click="cancelEditContact" />
                </div>
              </div>
              <div v-else>
                <button type="button" class="mb-3 text-xs text-n-brand hover:underline" @click="startEditContact">
                  <span class="i-lucide-pencil mr-1 inline-block align-middle text-[10px]" />
                  {{ t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.EDIT') }}
                </button>
              </div>

              <!-- Info grid -->
              <dl class="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                <div>
                  <dt class="text-n-slate-9">{{ t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.EMAIL') }}</dt>
                  <dd class="truncate font-medium text-n-slate-12">{{ selectedContact?.email || '-' }}</dd>
                </div>
                <div>
                  <dt class="text-n-slate-9">{{ t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.PHONE') }}</dt>
                  <dd class="truncate font-medium text-n-slate-12">{{ selectedContact?.phone_number || '-' }}</dd>
                </div>
                <div>
                  <dt class="text-n-slate-9">{{ t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.LAST_ACTIVITY') }}</dt>
                  <dd class="font-medium text-n-slate-12">{{ formatTimestamp(selectedContact?.last_activity_at) }}</dd>
                </div>
                <div>
                  <dt class="text-n-slate-9">{{ t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.CREATED_AT_LABEL') }}</dt>
                  <dd class="font-medium text-n-slate-12">{{ formatTimestamp(selectedContactCard?.created_at) }}</dd>
                </div>
              </dl>

              <!-- Deal value -->
              <div class="rounded-lg border border-n-alpha-3 bg-n-solid-1 p-3">
                <p class="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-n-slate-11">
                  <span class="i-lucide-dollar-sign text-sm text-emerald-500" />
                  {{ t('KANBAN_VIEW.DEAL.VALUE_LABEL') }}
                </p>
                <div v-if="editingDealCard === selectedContactCard?.id" class="flex items-center gap-2">
                  <input
                    v-model="dealValueInput"
                    type="number"
                    step="0.01"
                    min="0"
                    class="w-full rounded-lg border border-n-alpha-4 bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                    @keyup.enter="saveDealValue(selectedContactCard)"
                    @keyup.escape="cancelEditDealValue"
                    @blur="saveDealValue(selectedContactCard)"
                  />
                </div>
                <button
                  v-else
                  type="button"
                  class="w-full rounded-lg py-1 text-left text-lg font-bold transition-colors"
                  :class="selectedContactCard?.deal_value > 0 ? 'text-emerald-600 hover:text-emerald-700' : 'text-n-slate-9 hover:text-n-brand'"
                  @click="startEditDealValue(selectedContactCard)"
                >
                  {{ selectedContactCard?.deal_value > 0 ? formatCurrency(selectedContactCard.deal_value) : t('KANBAN_VIEW.DEAL.SET_VALUE') }}
                </button>
              </div>

              <!-- Return Date -->
              <div class="rounded-lg border border-n-alpha-3 bg-n-solid-1 p-3">
                <div class="mb-2 flex items-center justify-between">
                  <p class="flex items-center gap-1.5 text-xs font-semibold text-n-slate-11">
                    <span class="i-lucide-calendar-clock text-sm text-n-blue-11" />
                    Agendar retorno
                  </p>
                  <span
                    v-if="selectedContactCard?.return_date && selectedContactCard.return_date * 1000 < Date.now()"
                    class="rounded-full bg-ruby-3/30 px-2 py-0.5 text-[10px] font-semibold text-ruby-11"
                  >
                    Vencido
                  </span>
                </div>
                <input
                  v-model="returnDateInput"
                  type="datetime-local"
                  class="mb-2 w-full rounded-lg border border-n-alpha-4 bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                  :class="{ '!border-ruby-7': selectedContactCard?.return_date && selectedContactCard.return_date * 1000 < Date.now() }"
                />
                <div class="flex items-center justify-between">
                  <button
                    v-if="returnDateInput"
                    type="button"
                    class="text-[11px] text-n-slate-9 hover:text-ruby-11"
                    @click="returnDateInput = ''; saveReturnDate()"
                  >
                    Remover data
                  </button>
                  <span v-else />
                  <Button
                    size="xs"
                    color="blue"
                    icon="i-lucide-check"
                    label="Salvar"
                    :is-loading="isSavingReturnDate"
                    @click="saveReturnDate"
                  />
                </div>
                <p class="mt-2 flex items-center gap-1 text-[10px] text-n-slate-9">
                  <span class="i-lucide-bell text-[10px]" />
                  Alertas WhatsApp: configurar em
                  <button
                    type="button"
                    class="font-medium text-n-brand hover:underline"
                    @click="closeContactDetails(); $nextTick(() => { isPipelineSettingsOpen = true; openPipelineSettingsDialog(); })"
                  >
                    Config. do funil
                  </button>
                </p>
              </div>

              <!-- Move Stage -->
              <div class="rounded-lg border border-n-alpha-3 bg-n-solid-1 p-3">
                <p class="mb-2 flex items-center gap-1.5 text-xs font-semibold text-n-slate-11">
                  <span class="i-lucide-git-branch text-sm text-n-brand" />
                  Gerenciar etapa
                </p>
                <div class="mb-2 flex items-center gap-2 rounded-lg bg-n-alpha-2 px-3 py-2">
                  <span class="text-[11px] text-n-slate-10">Etapa atual:</span>
                  <span class="rounded-full bg-n-brand/10 px-2.5 py-0.5 text-xs font-semibold text-n-brand">
                    {{ stageNameById(selectedContactCard?.pipeline_stage_id) }}
                  </span>
                </div>
                <select
                  v-model="modalMoveTargetStageId"
                  class="mb-2 w-full rounded-lg border border-n-alpha-4 bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                >
                  <option :value="null" disabled>Mover para...</option>
                  <option
                    v-for="stage in boardStages"
                    :key="stage.id"
                    :value="stage.id"
                    :disabled="stage.id === selectedContactCard?.pipeline_stage_id"
                  >
                    {{ stage.name }}
                  </option>
                </select>
                <input
                  v-model="modalMoveNotes"
                  type="text"
                  placeholder="Observacao sobre a movimentacao (opcional)..."
                  class="mb-2 w-full rounded-lg border border-n-alpha-4 bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                />
                <Button
                  size="xs"
                  color="blue"
                  icon="i-lucide-arrow-right"
                  :label="t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.MOVE_BUTTON')"
                  :disabled="!modalMoveTargetStageId"
                  :is-loading="isMovingFromModal"
                  @click="moveFromModal"
                />
              </div>

              <!-- Labels -->
              <div class="rounded-lg border border-n-alpha-3 bg-n-solid-1 p-3">
                <p class="mb-2 flex items-center gap-1.5 text-xs font-semibold text-n-slate-11">
                  <span class="i-lucide-tags text-sm" />
                  {{ t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.LABELS') }}
                </p>
                <div v-if="contactLabels.length" class="mb-2 flex flex-wrap gap-1">
                  <span
                    v-for="label in contactLabels"
                    :key="label"
                    class="group inline-flex items-center gap-1 rounded-full bg-n-brand/10 px-2.5 py-1 text-[11px] font-medium text-n-brand"
                  >
                    {{ label }}
                    <button
                      type="button"
                      class="i-lucide-x rounded-full text-[9px] opacity-0 transition group-hover:opacity-100 hover:text-ruby-11"
                      @click="removeContactLabel(label)"
                    />
                  </span>
                </div>
                <div class="flex gap-2">
                  <input
                    v-model="newContactLabel"
                    type="text"
                    :placeholder="t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.ADD_LABEL')"
                    class="w-full rounded-lg border border-n-alpha-4 bg-n-solid-1 px-3 py-1.5 text-xs text-n-slate-12 placeholder:text-n-slate-9 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                    @keyup.enter="addContactLabel"
                  />
                  <Button size="xs" color="blue" icon="i-lucide-plus" :is-loading="isUpdatingLabels" @click="addContactLabel" />
                </div>
              </div>

              <InstagramLeadScoreCard v-if="selectedContact" :contact="selectedContact" />

              <!-- Custom Attributes -->
              <div
                v-if="selectedPipeline?.card_display_attributes?.filter(a => a.attribute_key).length"
                class="rounded-lg border border-n-alpha-3 bg-n-solid-1 p-3"
              >
                <p class="mb-2 text-xs font-medium text-n-slate-9">{{ t('KANBAN_VIEW.STAGE_CONFIG.CARD_DISPLAY_TITLE') }}</p>
                <dl class="space-y-2">
                  <div
                    v-for="attrConfig in selectedPipeline.card_display_attributes.filter(a => a.attribute_key)"
                    :key="attrConfig.attribute_key"
                    class="flex items-center justify-between gap-2"
                  >
                    <dt class="shrink-0 text-xs text-n-slate-10">{{ attrConfig.label }}</dt>
                    <dd>
                      <CardAttributeInlineEditor
                        v-if="selectedContactCard"
                        :card="selectedContactCard"
                        :attr-config="attrConfig"
                        :attr-def="allCustomAttributes.find(a => a.attribute_key === attrConfig.attribute_key && a.attribute_model === attrConfig.attribute_model)"
                        @updated="val => {
                          if (!selectedContactCard?.item) return;
                          if (attrConfig.attribute_model === 'contact_attribute' && selectedContactCard.item_type === 'Conversation') {
                            selectedContactCard.item.contact_custom_attributes = { ...(selectedContactCard.item.contact_custom_attributes || {}), [attrConfig.attribute_key]: val };
                          } else if (attrConfig.attribute_model === 'conversation_attribute' && selectedContactCard.item_type === 'Contact') {
                            selectedContactCard.item.conversation_custom_attributes = { ...(selectedContactCard.item.conversation_custom_attributes || {}), [attrConfig.attribute_key]: val };
                          } else {
                            selectedContactCard.item.custom_attributes = { ...(selectedContactCard.item.custom_attributes || {}), [attrConfig.attribute_key]: val };
                          }
                        }"
                      />
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </aside>

          <!-- RIGHT SECTION: Tabbed content -->
          <section class="flex min-w-0 flex-1 flex-col">
            <!-- Tab Bar -->
            <nav class="flex shrink-0 gap-0.5 border-b border-n-alpha-3 px-4 pt-1">
              <button
                v-for="tab in [
                  { key: 'conversations', icon: 'i-lucide-message-square', label: 'Conversas', count: contactConversations.length },
                  { key: 'notes', icon: 'i-lucide-sticky-note', label: 'Notas', count: itemNotes.length + contactNotes.length },
                  { key: 'dados', icon: 'i-lucide-building-2', label: 'Dados', count: filledCustomAttributesCount },
                  { key: 'tasks', icon: 'i-lucide-check-square', label: 'Tarefas', count: (selectedContactCard?.metadata?.tasks || []).length },
                  { key: 'history', icon: 'i-lucide-git-commit', label: 'Historico', count: itemMoves.length },
                  { key: 'products', icon: 'i-lucide-package', label: 'Produtos', count: (selectedContactCard?.product_ids || []).length },
                  { key: 'documents', icon: 'i-lucide-file-text', label: 'Documentos', count: itemDocuments.length },
                ]"
                :key="tab.key"
                type="button"
                class="relative flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors"
                :class="modalActiveTab === tab.key
                  ? 'text-n-blue-11'
                  : 'text-n-slate-10 hover:text-n-slate-12'"
                @click="modalActiveTab = tab.key"
              >
                <span :class="tab.icon" class="text-sm" />
                {{ tab.label }}
                <span
                  v-if="tab.count > 0"
                  class="min-w-[18px] rounded-full px-1 py-0.5 text-center text-[10px] font-semibold leading-none"
                  :class="modalActiveTab === tab.key ? 'bg-n-blue-9 text-white' : 'bg-n-alpha-3 text-n-slate-10'"
                >
                  {{ tab.count }}
                </span>
                <span
                  v-if="modalActiveTab === tab.key"
                  class="absolute bottom-0 left-2 right-2 h-0.5 rounded-t bg-n-blue-9"
                />
              </button>
            </nav>

            <!-- Tab Content -->
            <div class="flex-1 overflow-y-auto p-4">
              <!-- ─── TAB: Conversations ─── -->
              <div v-if="modalActiveTab === 'conversations'">
                <div v-if="isLoadingContactDetails" class="flex items-center justify-center py-12 text-sm text-n-slate-11">
                  <span class="i-lucide-loader-2 mr-2 animate-spin" />
                  {{ t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.LOADING') }}
                </div>
                <div v-else-if="!contactConversations.length" class="flex flex-col items-center justify-center py-12 text-n-slate-10">
                  <span class="i-lucide-message-square-off mb-2 text-3xl" />
                  <p class="text-sm">{{ t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.NO_CONVERSATIONS') }}</p>
                </div>
                <div v-else class="flex gap-4">
                  <!-- Conversation list (left) -->
                  <div class="w-64 shrink-0 space-y-1 overflow-y-auto rounded-lg border border-n-alpha-3 bg-n-alpha-1/50 p-2">
                    <div v-for="group in conversationsByInbox" :key="group.inbox_id" class="mb-2 last:mb-0">
                      <p class="mb-1 flex items-center gap-1.5 px-2 text-[10px] font-bold uppercase tracking-wider text-n-slate-9">
                        <span class="i-lucide-inbox text-xs" />
                        {{ group.inbox_name }}
                        <span class="ml-auto rounded bg-n-alpha-3 px-1 py-0.5 text-[9px]">{{ group.conversations.length }}</span>
                      </p>
                      <div class="space-y-0.5">
                        <button
                          v-for="conv in group.conversations"
                          :key="conv.id"
                          type="button"
                          class="w-full rounded-lg px-2.5 py-2 text-left transition-all"
                          :class="selectedConversationId === conv.id
                            ? 'bg-n-blue-3/30 ring-1 ring-n-blue-7/50'
                            : 'hover:bg-n-alpha-2'"
                          @click="selectConversation(conv.id)"
                        >
                          <div class="flex items-center justify-between">
                            <p class="text-xs font-semibold text-n-slate-12">#{{ conv.id }}</p>
                            <span
                              class="rounded-full px-1.5 py-0.5 text-[9px] font-medium"
                              :class="conv.status === 'open' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-n-alpha-2 text-n-slate-9'"
                            >
                              {{ conv.status === 'open' ? 'Aberta' : conv.status === 'resolved' ? 'Resolvida' : conv.status }}
                            </span>
                          </div>
                          <p class="mt-0.5 truncate text-[11px] text-n-slate-10">
                            {{ conv.last_non_activity_message?.content || conv.messages?.[0]?.content || '-' }}
                          </p>
                        </button>
                      </div>
                    </div>
                  </div>
                  <!-- Messages (right) -->
                  <div class="min-w-0 flex-1">
                    <div class="mb-3 flex items-center justify-between">
                      <p class="text-xs font-semibold text-n-slate-11">
                        {{ t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.LAST_MESSAGES') }}
                      </p>
                      <div class="flex items-center gap-2">
                        <Button
                          size="xs"
                          color="teal"
                          variant="outline"
                          icon="i-lucide-calendar-clock"
                          :label="t('CONVERSATION.SCHEDULED_MESSAGE.ACTIONS.OPEN')"
                          :disabled="!selectedConversation"
                          @click="openScheduleDialogFromKanban"
                        />
                        <Button
                          size="xs"
                          color="blue"
                          variant="outline"
                          :label="t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.OPEN_CONVERSATION')"
                          :disabled="!selectedConversation"
                          @click="openConversationFromModal(selectedConversation?.id)"
                        />
                      </div>
                    </div>
                    <div v-if="isLoadingMessages" class="flex items-center justify-center py-12 text-sm text-n-slate-11">
                      <span class="i-lucide-loader-2 mr-2 animate-spin" />
                      {{ t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.LOADING') }}
                    </div>
                    <div v-else-if="!conversationMessages.length" class="py-8 text-center text-sm text-n-slate-10">
                      {{ t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.NO_MESSAGES') }}
                    </div>
                    <div v-else class="space-y-1.5">
                      <div
                        v-for="message in conversationMessages"
                        :key="message.id"
                        class="flex gap-2"
                        :class="message.message_type === 1 ? 'flex-row-reverse' : ''"
                      >
                        <div
                          class="max-w-[80%] rounded-xl p-3"
                          :class="message.message_type === 1
                            ? 'rounded-tr-sm bg-n-blue-3/30'
                            : 'rounded-tl-sm bg-n-alpha-2'"
                        >
                          <p class="mb-0.5 text-[11px] font-semibold" :class="message.message_type === 1 ? 'text-n-blue-11' : 'text-n-slate-11'">
                            {{ message.sender?.name || '-' }}
                          </p>
                          <p class="whitespace-pre-wrap text-sm leading-relaxed text-n-slate-12">{{ message.content || '-' }}</p>
                          <p class="mt-1 text-right text-[10px] text-n-slate-9">
                            {{ formatMessageTimestamp(message.created_at) }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ─── TAB: Notes ─── -->
              <div v-if="modalActiveTab === 'notes'" class="space-y-4">
                <!-- Add note form -->
                <div class="rounded-xl border border-n-alpha-3 bg-n-alpha-1/50 p-4">
                  <textarea
                    v-model="newNoteContent"
                    rows="3"
                    :placeholder="t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.ADD_NOTE_PLACEHOLDER')"
                    class="w-full resize-none rounded-lg border border-n-alpha-4 bg-n-solid-1 px-4 py-3 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                    @keydown.meta.enter="addItemNote"
                    @keydown.ctrl.enter="addItemNote"
                  />
                  <div class="mt-2 flex items-center justify-between">
                    <p class="text-[11px] text-n-slate-9">Ctrl+Enter para salvar</p>
                    <Button
                      size="sm"
                      color="blue"
                      icon="i-lucide-send"
                      label="Salvar nota"
                      :is-loading="isSavingNote"
                      :disabled="!newNoteContent?.trim()"
                      @click="addItemNote"
                    />
                  </div>
                </div>
                <!-- Notes list -->
                <div class="space-y-2">
                  <div
                    v-for="note in [...itemNotes].reverse()"
                    :key="'item-' + note.id"
                    class="rounded-lg border border-n-alpha-3 bg-n-solid-1 p-4"
                  >
                    <p class="whitespace-pre-wrap text-sm leading-relaxed text-n-slate-12">{{ note.content }}</p>
                    <div class="mt-3 flex items-center gap-2 border-t border-n-alpha-2 pt-2 text-xs text-n-slate-9">
                      <span class="i-lucide-user text-[10px]" />
                      <span>{{ note.created_by_name }}</span>
                      <span>&middot;</span>
                      <span>{{ formatTimestamp(note.created_at) }}</span>
                    </div>
                  </div>
                  <div
                    v-for="note in contactNotes"
                    :key="'contact-' + note.id"
                    class="rounded-lg border border-n-amber-5/30 bg-n-amber-2/10 p-4"
                  >
                    <p class="whitespace-pre-wrap text-sm leading-relaxed text-n-slate-12">{{ note.content }}</p>
                    <div class="mt-3 flex items-center gap-2 border-t border-n-amber-4/20 pt-2 text-xs text-n-slate-9">
                      <span class="i-lucide-user text-[10px]" />
                      <span>{{ note.user?.name || 'Sistema' }}</span>
                      <span>&middot;</span>
                      <span>{{ formatTimestamp(note.created_at) }}</span>
                      <span class="rounded-full bg-n-amber-3/40 px-2 py-0.5 text-[10px] font-semibold text-n-amber-11">Nota do contato</span>
                    </div>
                  </div>
                  <div v-if="!itemNotes.length && !contactNotes.length" class="flex flex-col items-center py-12 text-n-slate-10">
                    <span class="i-lucide-sticky-note mb-3 text-4xl opacity-40" />
                    <p class="text-sm font-medium">Nenhuma anotacao ainda</p>
                    <p class="mt-1 text-xs">Adicione notas acima para registrar observacoes</p>
                  </div>
                </div>
              </div>

              <!-- ─── TAB: Dados (Custom Attributes) ─── -->
              <div v-if="modalActiveTab === 'dados'">
                <div v-if="!filledCustomAttributes.length" class="flex flex-col items-center py-12 text-n-slate-10">
                  <span class="i-lucide-building-2 mb-3 text-4xl opacity-40" />
                  <p class="text-sm font-medium">Nenhum dado preenchido</p>
                  <p class="mt-1 text-xs">Os atributos do contato aparecerão aqui quando preenchidos</p>
                </div>
                <div v-else class="space-y-3">
                  <!-- Group: Company Info -->
                  <div v-if="hasAttrGroup('company')" class="rounded-xl border border-n-alpha-3 bg-n-alpha-1/50 p-4">
                    <p class="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-n-slate-10">
                      <span class="i-lucide-building-2 text-sm" />
                      Empresa
                    </p>
                    <dl class="grid grid-cols-2 gap-x-6 gap-y-2.5">
                      <template v-for="[key, val] in filledCustomAttributes.filter(([k]) => companyAttrKeys.includes(k))" :key="key">
                        <dt class="text-xs text-n-slate-9">{{ attrLabel(key) }}</dt>
                        <dd class="text-xs font-medium text-n-slate-12">{{ formatAttrValue(key, val) }}</dd>
                      </template>
                    </dl>
                  </div>
                  <!-- Group: Location -->
                  <div v-if="hasAttrGroup('location')" class="rounded-xl border border-n-alpha-3 bg-n-alpha-1/50 p-4">
                    <p class="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-n-slate-10">
                      <span class="i-lucide-map-pin text-sm" />
                      Localizacao
                    </p>
                    <dl class="grid grid-cols-2 gap-x-6 gap-y-2.5">
                      <template v-for="[key, val] in filledCustomAttributes.filter(([k]) => locationAttrKeys.includes(k))" :key="key">
                        <dt class="text-xs text-n-slate-9">{{ attrLabel(key) }}</dt>
                        <dd class="text-xs font-medium text-n-slate-12">{{ formatAttrValue(key, val) }}</dd>
                      </template>
                    </dl>
                  </div>
                  <!-- Group: Financial -->
                  <div v-if="hasAttrGroup('financial')" class="rounded-xl border border-n-alpha-3 bg-n-alpha-1/50 p-4">
                    <p class="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-n-slate-10">
                      <span class="i-lucide-banknote text-sm" />
                      Financeiro
                    </p>
                    <dl class="grid grid-cols-2 gap-x-6 gap-y-2.5">
                      <template v-for="[key, val] in filledCustomAttributes.filter(([k]) => financialAttrKeys.includes(k))" :key="key">
                        <dt class="text-xs text-n-slate-9">{{ attrLabel(key) }}</dt>
                        <dd class="text-xs font-medium text-n-slate-12">{{ formatAttrValue(key, val) }}</dd>
                      </template>
                    </dl>
                  </div>
                  <!-- Group: Other attributes -->
                  <div v-if="hasAttrGroup('other')" class="rounded-xl border border-n-alpha-3 bg-n-alpha-1/50 p-4">
                    <p class="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-n-slate-10">
                      <span class="i-lucide-list text-sm" />
                      Outros
                    </p>
                    <dl class="grid grid-cols-2 gap-x-6 gap-y-2.5">
                      <template v-for="[key, val] in filledCustomAttributes.filter(([k]) => !companyAttrKeys.includes(k) && !locationAttrKeys.includes(k) && !financialAttrKeys.includes(k))" :key="key">
                        <dt class="text-xs text-n-slate-9">{{ attrLabel(key) }}</dt>
                        <dd class="text-xs font-medium text-n-slate-12 break-words">{{ formatAttrValue(key, val) }}</dd>
                      </template>
                    </dl>
                  </div>
                </div>
              </div>

              <!-- ─── TAB: Tasks ─── -->
              <div v-if="modalActiveTab === 'tasks'" class="space-y-4">
                <!-- Add task form -->
                <div class="rounded-xl border border-n-alpha-3 bg-n-alpha-1/50 p-4">
                  <p class="mb-3 text-xs font-semibold uppercase tracking-wide text-n-slate-10">Nova tarefa</p>
                  <div class="space-y-3">
                    <input
                      v-model="newTaskTitle"
                      type="text"
                      placeholder="Ex: Ligar para o cliente, Enviar proposta..."
                      class="w-full rounded-lg border border-n-alpha-4 bg-n-solid-1 px-4 py-2.5 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                      @keyup.enter="addTask"
                    />
                    <div class="flex items-end gap-3">
                      <div class="flex-1">
                        <label class="mb-1 block text-[11px] text-n-slate-10">
                          <span class="i-lucide-clock mr-1 inline-block align-middle text-[10px]" />
                          Vencimento (opcional)
                        </label>
                        <input
                          v-model="newTaskDueDate"
                          type="datetime-local"
                          class="w-full rounded-lg border border-n-alpha-4 bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                        />
                      </div>
                      <Button
                        size="sm"
                        color="blue"
                        icon="i-lucide-plus"
                        :label="t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.TASK_ADD')"
                        :disabled="!newTaskTitle?.trim()"
                        @click="addTask"
                      />
                    </div>
                  </div>
                </div>

                <!-- Task summary -->
                <div v-if="(selectedContactCard?.metadata?.tasks || []).length" class="flex items-center gap-4 text-xs text-n-slate-10">
                  <span>
                    <span class="font-semibold text-n-slate-12">{{ (selectedContactCard?.metadata?.tasks || []).filter(t => t.done).length }}</span>
                    / {{ (selectedContactCard?.metadata?.tasks || []).length }} concluidas
                  </span>
                  <div class="h-1.5 flex-1 overflow-hidden rounded-full bg-n-alpha-3">
                    <div
                      class="h-full rounded-full bg-emerald-500 transition-all duration-300"
                      :style="{ width: `${((selectedContactCard?.metadata?.tasks || []).filter(t => t.done).length / (selectedContactCard?.metadata?.tasks || []).length) * 100}%` }"
                    />
                  </div>
                </div>

                <!-- Task list -->
                <div class="space-y-1.5">
                  <div
                    v-for="task in (selectedContactCard?.metadata?.tasks || [])"
                    :key="task.id"
                    class="group flex items-center gap-3 rounded-lg border border-n-alpha-3 bg-n-solid-1 px-4 py-3 transition-colors hover:border-n-alpha-4"
                  >
                    <button
                      type="button"
                      class="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors"
                      :class="task.done
                        ? 'border-emerald-500 bg-emerald-500 text-white'
                        : 'border-n-alpha-5 hover:border-n-blue-7'"
                      @click="toggleTaskDone(task.id)"
                    >
                      <span v-if="task.done" class="i-lucide-check text-xs" />
                    </button>
                    <div class="min-w-0 flex-1">
                      <p
                        class="text-sm"
                        :class="task.done ? 'line-through text-n-slate-9' : 'text-n-slate-12'"
                      >
                        {{ task.title }}
                      </p>
                      <p v-if="task.due_date" class="mt-0.5 flex items-center gap-1 text-[11px]">
                        <span class="i-lucide-clock text-[10px]" />
                        <span
                          :class="new Date(task.due_date) < new Date() && !task.done
                            ? 'font-medium text-ruby-11'
                            : 'text-n-slate-10'"
                        >
                          {{ formatTimestamp(Math.floor(new Date(task.due_date).getTime() / 1000)) }}
                          <template v-if="new Date(task.due_date) < new Date() && !task.done"> (vencida)</template>
                        </span>
                      </p>
                    </div>
                    <button
                      type="button"
                      class="shrink-0 rounded-lg p-1.5 text-n-slate-9 opacity-0 transition group-hover:opacity-100 hover:bg-ruby-3/30 hover:text-ruby-11"
                      @click="removeTask(task.id)"
                    >
                      <span class="i-lucide-trash-2 text-sm" />
                    </button>
                  </div>
                </div>
                <div v-if="!(selectedContactCard?.metadata?.tasks || []).length" class="flex flex-col items-center py-12 text-n-slate-10">
                  <span class="i-lucide-check-square mb-3 text-4xl opacity-40" />
                  <p class="text-sm font-medium">Nenhuma tarefa adicionada</p>
                  <p class="mt-1 text-xs">Adicione tarefas acima para organizar suas atividades</p>
                </div>
              </div>

              <!-- ─── TAB: History ─── -->
              <div v-if="modalActiveTab === 'history'">
                <div v-if="isLoadingMoves" class="flex items-center justify-center py-12 text-sm text-n-slate-11">
                  <span class="i-lucide-loader-2 mr-2 animate-spin" />
                  {{ t('KANBAN_VIEW.DIALOGS.CONTACT_DETAIL.LOADING') }}
                </div>
                <div v-else-if="!itemMoves.length" class="flex flex-col items-center py-12 text-n-slate-10">
                  <span class="i-lucide-git-commit mb-3 text-4xl opacity-40" />
                  <p class="text-sm font-medium">Nenhuma movimentacao registrada</p>
                  <p class="mt-1 text-xs">O historico aparecera quando o card for movido entre etapas</p>
                </div>
                <!-- Timeline -->
                <div v-else class="relative ml-5 border-l-2 border-n-alpha-3 space-y-0">
                  <div
                    v-for="(move, idx) in itemMoves"
                    :key="move.id"
                    class="relative pb-6 pl-7 last:pb-0"
                  >
                    <span
                      class="absolute -left-[9px] top-0 flex h-[18px] w-[18px] items-center justify-center rounded-full"
                      :class="idx === 0 ? 'bg-n-blue-9 ring-4 ring-n-blue-3/20' : 'bg-n-alpha-5'"
                    >
                      <span class="i-lucide-arrow-right text-[10px] text-white" />
                    </span>
                    <div class="rounded-lg border border-n-alpha-3 bg-n-solid-1 p-3">
                      <div class="flex items-center gap-2">
                        <span class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-10">
                          {{ stageNameById(move.from_stage_id) }}
                        </span>
                        <span class="i-lucide-arrow-right text-xs text-n-slate-9" />
                        <span class="rounded-full bg-n-blue-3/30 px-2 py-0.5 text-xs font-semibold text-n-blue-11">
                          {{ stageNameById(move.to_stage_id) }}
                        </span>
                      </div>
                      <p v-if="move.notes" class="mt-2 rounded bg-n-alpha-1 px-3 py-1.5 text-sm italic text-n-slate-10">
                        "{{ move.notes }}"
                      </p>
                      <p class="mt-2 text-xs text-n-slate-9">
                        <span class="i-lucide-user mr-1 inline-block align-middle text-[10px]" />
                        {{ move.moved_by_name }} &middot; {{ formatTimestamp(move.moved_at) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ─── TAB: Products ─── -->
              <div v-if="modalActiveTab === 'products'">
                <div v-if="!hasProductsCatalog || !products.length" class="flex flex-col items-center py-12 text-n-slate-10">
                  <span class="i-lucide-package mb-3 text-4xl opacity-40" />
                  <p class="text-sm font-medium">Nenhum produto disponivel no catalogo</p>
                  <p class="mt-1 text-xs">Adicione produtos no modulo Catalogo primeiro</p>
                </div>
                <div v-else class="space-y-4">
                  <!-- Totals bar -->
                  <div class="grid grid-cols-2 gap-3">
                    <div class="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                      <p class="mb-1 flex items-center gap-1.5 text-xs text-n-slate-10">
                        <span class="i-lucide-dollar-sign text-sm text-emerald-500" />
                        Valor total da negociacao
                      </p>
                      <p class="text-2xl font-bold text-emerald-600">{{ formatCurrency(computedDealValue) }}</p>
                    </div>
                    <div class="rounded-xl border border-n-blue-7/20 bg-n-blue-2/10 p-4">
                      <p class="mb-1 flex items-center gap-1.5 text-xs text-n-slate-10">
                        <span class="i-lucide-trending-up text-sm text-n-blue-11" />
                        Comissao estimada
                      </p>
                      <p class="text-2xl font-bold text-n-blue-11">{{ formatCurrency(computedCommission) }}</p>
                    </div>
                  </div>
                  <!-- Product list -->
                  <div class="space-y-2">
                    <div
                      v-for="p in products"
                      :key="p.id"
                      class="group flex items-center gap-3 rounded-lg border p-3 transition-all"
                      :class="(selectedContactCard?.product_ids || []).includes(p.id)
                        ? 'border-n-blue-7/40 bg-n-blue-2/10'
                        : 'border-n-alpha-3 bg-n-solid-1 hover:border-n-alpha-4'"
                    >
                      <button
                        type="button"
                        class="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors"
                        :class="(selectedContactCard?.product_ids || []).includes(p.id)
                          ? 'border-n-blue-9 bg-n-blue-9 text-white'
                          : 'border-n-alpha-5 hover:border-n-blue-7'"
                        @click="toggleProduct(selectedContactCard, p.id)"
                      >
                        <span v-if="(selectedContactCard?.product_ids || []).includes(p.id)" class="i-lucide-check text-xs" />
                      </button>
                      <span v-if="p.color" class="inline-block h-3 w-3 shrink-0 rounded-full ring-2 ring-white/20" :style="{ backgroundColor: p.color }" />
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                          <p class="text-sm font-medium text-n-slate-12">{{ p.name }}</p>
                          <span
                            v-if="p.category && CATEGORY_MAP[p.category]"
                            class="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none"
                            :class="CATEGORY_MAP[p.category].color"
                          >{{ CATEGORY_MAP[p.category].label }}</span>
                        </div>
                        <div v-if="p.price > 0" class="mt-0.5 flex items-center gap-2 text-xs text-n-slate-9">
                          <span>Ref: {{ formatCurrency(p.price) }}</span>
                          <span v-if="p.commission_value > 0" class="rounded bg-n-alpha-2 px-1.5 py-0.5">
                            Comissao: {{ p.commission_type === 'percentage' ? `${p.commission_value}%` : formatCurrency(p.commission_value) }}
                          </span>
                        </div>
                      </div>
                      <div v-if="(selectedContactCard?.product_ids || []).includes(p.id)" class="w-36">
                        <label class="mb-0.5 block text-[10px] text-n-slate-9">Valor personalizado</label>
                        <input
                          v-model="productValues[p.id]"
                          type="number"
                          step="0.01"
                          min="0"
                          :placeholder="p.price > 0 ? formatCurrency(p.price) : 'Valor'"
                          class="w-full rounded-lg border border-n-alpha-4 bg-n-solid-1 px-3 py-1.5 text-right text-sm text-n-slate-12 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                          @blur="saveProductValues"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- DOCUMENTS TAB -->
              <div v-if="modalActiveTab === 'documents'" class="space-y-4">
                <!-- Upload form -->
                <div class="rounded-xl border border-n-alpha-3 bg-n-solid-1 p-4">
                  <p class="mb-3 text-xs font-semibold uppercase tracking-wider text-n-slate-10">Novo documento</p>
                  <div class="space-y-3">
                    <input
                      v-model="newDocTitle"
                      type="text"
                      placeholder="Titulo do documento"
                      class="w-full rounded-lg border border-n-alpha-4 bg-n-background px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-8 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                    />
                    <input
                      ref="docFileInputRef"
                      type="file"
                      accept=".pdf,.doc,.docx,.md,.txt,.xls,.xlsx,.csv,.ppt,.pptx,.odt,.rtf"
                      class="w-full text-sm text-n-slate-10 file:mr-3 file:rounded-lg file:border-0 file:bg-n-blue-2/20 file:px-3 file:py-2 file:text-sm file:font-medium file:text-n-blue-11 hover:file:bg-n-blue-2/40"
                      @change="onDocFileChange"
                    />
                    <button
                      type="button"
                      :disabled="!newDocTitle.trim() || !newDocFile || docUploading"
                      class="flex items-center gap-2 rounded-lg bg-n-blue-9 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-n-blue-10 disabled:cursor-not-allowed disabled:opacity-40"
                      @click="uploadDocument"
                    >
                      <span v-if="docUploading" class="i-lucide-loader-2 animate-spin text-sm" />
                      <span v-else class="i-lucide-upload text-sm" />
                      {{ docUploading ? 'Enviando...' : 'Enviar' }}
                    </button>
                  </div>
                </div>

                <!-- Documents list -->
                <div v-if="documentsLoading" class="flex items-center justify-center py-8 text-n-slate-9">
                  <span class="i-lucide-loader-2 mr-2 animate-spin" /> Carregando...
                </div>
                <div v-else-if="itemDocuments.length === 0" class="py-8 text-center text-sm text-n-slate-9">
                  Nenhum documento anexado
                </div>
                <div v-else class="space-y-2">
                  <div
                    v-for="doc in itemDocuments"
                    :key="doc.id"
                    class="group flex items-center gap-3 rounded-lg border border-n-alpha-3 bg-n-solid-1 p-3 transition-all hover:border-n-alpha-4"
                  >
                    <span class="i-lucide-file-text shrink-0 text-lg text-n-blue-11" />
                    <div class="min-w-0 flex-1">
                      <p class="truncate text-sm font-medium text-n-slate-12">{{ doc.title }}</p>
                      <div class="mt-0.5 flex items-center gap-2 text-xs text-n-slate-9">
                        <span>{{ doc.file?.filename }}</span>
                        <span v-if="doc.file?.byte_size">· {{ formatFileSize(doc.file.byte_size) }}</span>
                        <span v-if="doc.uploaded_by">· {{ doc.uploaded_by }}</span>
                      </div>
                    </div>
                    <div class="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <a
                        v-if="doc.file?.url"
                        :href="doc.file.url"
                        target="_blank"
                        class="flex h-8 w-8 items-center justify-center rounded-lg text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-blue-11"
                        title="Abrir"
                      >
                        <span class="i-lucide-external-link text-sm" />
                      </a>
                      <button
                        type="button"
                        class="flex h-8 w-8 items-center justify-center rounded-lg text-n-slate-10 transition-colors hover:bg-rose-50 hover:text-rose-600"
                        title="Excluir"
                        @click="deleteDocument(doc.id)"
                      >
                        <span class="i-lucide-trash-2 text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>

    <!-- Quick Contact Dialog -->
    <div
      v-if="showQuickContactDialog"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      @click.self="showQuickContactDialog = false"
    >
      <div class="w-full max-w-md rounded-2xl border border-n-alpha-3 bg-n-solid-2 p-6 shadow-2xl">
        <div class="mb-5 flex items-center gap-3">
          <span class="flex h-10 w-10 items-center justify-center rounded-xl bg-n-teal-9/10 text-n-teal-11">
            <span class="i-lucide-user-plus text-lg" />
          </span>
          <div>
            <h3 class="text-base font-semibold text-n-slate-12">Criar Contato</h3>
            <p class="text-xs text-n-slate-9">Adiciona ao funil na primeira etapa</p>
          </div>
        </div>
        <div class="space-y-3">
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">Nome *</label>
            <input
              v-model="quickContactForm.name"
              type="text"
              placeholder="Nome completo"
              class="w-full rounded-lg border border-n-alpha-4 bg-n-background px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-8 focus:border-n-teal-8 focus:outline-none focus:ring-2 focus:ring-n-teal-8/30"
              @keyup.enter="createQuickContact"
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">Telefone *</label>
            <PhoneNumberInput
              v-model="quickContactForm.phone"
              placeholder="DDD + Numero"
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">Responsavel</label>
            <select
              v-model="quickContactForm.assignee_id"
              class="w-full rounded-lg border border-n-alpha-4 bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:border-n-teal-8 focus:outline-none focus:ring-2 focus:ring-n-teal-8/30"
            >
              <option :value="null">Nenhum</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-lg border border-n-alpha-4 px-4 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-1"
            @click="showQuickContactDialog = false"
          >
            Cancelar
          </button>
          <button
            type="button"
            :disabled="!quickContactForm.name.trim() || !quickContactForm.phone || quickContactSaving"
            class="flex items-center gap-2 rounded-lg bg-n-teal-9 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-n-teal-10 disabled:cursor-not-allowed disabled:opacity-40"
            @click="createQuickContact"
          >
            <span v-if="quickContactSaving" class="i-lucide-loader-2 animate-spin text-sm" />
            <span v-else class="i-lucide-check text-sm" />
            {{ quickContactSaving ? 'Criando...' : 'Criar' }}
          </button>
        </div>
      </div>
    </div>

    <ScheduleMessageDialog
      ref="scheduleDialogRef"
      :conversation-id="selectedConversation?.id"
    />

    <woot-modal
      v-model:show="showAddAutomationModal"
      size="medium"
      :on-close="hideAddAutomationModal"
    >
      <AddAutomationRule
        v-if="showAddAutomationModal"
        :on-close="hideAddAutomationModal"
        :initial-automation="automationPreset"
        @save-automation="submitAutomationFromKanban"
      />
    </woot-modal>

    <woot-modal
      v-model:show="showEditAutomationModal"
      size="medium"
      :on-close="hideEditAutomationModal"
    >
      <EditAutomationRule
        v-if="showEditAutomationModal && automationToEdit"
        :on-close="hideEditAutomationModal"
        :selected-response="automationToEdit"
        @save-automation="submitEditedAutomation"
      />
    </woot-modal>
  </div>
</template>
